import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { ClientContextBanner, ClientContextIndicator } from '../../components/accountant/ClientContextBanner';
import AttachmentManager from '../../components/common/AttachmentManager';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import LayoutPreviewButton from '../../components/LayoutPreviewButton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import QuotationAttachmentField from '../../components/common/QuotationAttachmentField';
import BoqUploadDialog from '../../components/sales/BoqUploadDialog';
import RevisionDiffViewer from '../../components/sales/RevisionDiffViewer';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Plus, 
  CircleNotch,
  Trash,
  FileText,
  Receipt,
  ArrowRight,
  CheckCircle,
  Users,
  UserPlus,
  Percent,
  MagnifyingGlass,
  Eye,
  Download,
  Paperclip,
  X,
  File,
  FilePdf,
  UploadSimple,
  PencilSimple,
  PaperPlaneTilt,
  XCircle,
  Clock,
  Warning,
  Copy as CopySimple,
  DotsThreeVertical,
  CurrencyDollar as Money,
  Sparkle,
  ClockClockwise,
  ChartBar
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import DocSummary from '../../components/DocSummary';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Amount this invoice bills — mirrors backend billing_utils.invoice_billed
const invoiceBilled = (inv) => {
  const total = Number(inv?.total || 0);
  const prev = Number(inv?.previous_payments || 0);
  const settledHere = inv?.advance_settled_here ?? (!!inv?.is_part_invoice && Number(inv?.advance_payment || 0) > 0);
  const adv = settledHere ? 0 : Number(inv?.advance_payment || 0);
  return prev + adv > 0.009 ? Math.max(total - prev - adv, 0) : total;
};

const statusColors = {
  draft: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
  pending_approval: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  approved: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  rejected: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  sent_to_client: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  client_approved: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  agreement_signed: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  lpo_received: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  on_hold: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  closed: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-500 border-zinc-500/20',
  invoiced: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  sent: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  paid: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  partially_paid: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
  overdue: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  partial: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

const statusLabels = {
  draft: 'Draft', pending_approval: 'Pending Approval', approved: 'Approved',
  rejected: 'Rejected', sent_to_client: 'Sent to Client', sent: 'Sent',
  client_approved: 'Approved by Client',
  agreement_signed: 'Agreement Signed', lpo_received: 'LPO Received',
  on_hold: 'On Hold', closed: 'Closed', invoiced: 'Invoiced',
  paid: 'Paid', partially_paid: 'Partially Paid',
  overdue: 'Overdue', partial: 'Partially Paid', cancelled: 'Cancelled',
  void: 'Void',
};

// A quotation can be edited in-place (no revision) until status reaches one of these.
const QUOTE_LOCKED_STATUSES = ['client_approved', 'agreement_signed', 'lpo_received', 'invoiced', 'closed', 'cancelled'];
const isQuoteEditable = (status) => !QUOTE_LOCKED_STATUSES.includes(status);

export default function SalesPage() {
  const { hasPermission, user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('invoices');
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createType, setCreateType] = useState('invoice');
  const [formLoading, setFormLoading] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  
  // PDF Preview Modal State
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');

  // BOQ Upload (AI-assisted line-item import) State
  const [boqUploadOpen, setBoqUploadOpen] = useState(false);

  // Payment Terms — preset vs custom-typed
  const PAYMENT_TERMS_PRESETS = ['advance', '50_advance', 'cod', 'net15', 'net30', 'net45', 'net60', 'credit'];
  const [customPaymentTermsMode, setCustomPaymentTermsMode] = useState(false);

  // Quotation-to-Invoice approval guard
  // -------------------------------------
  // When a user picks a quotation in the "Link to Quotation" dropdown that is
  // not yet "client approved" / "agreement signed" / "lpo received", we show a
  // confirmation dialog reminding them to update the quote's status BEFORE the
  // invoice is raised. This prevents accidental billing against an unapproved
  // quote.
  const QUOTE_APPROVED_STATUSES = ['client_approved', 'agreement_signed', 'lpo_received'];
  // VALID_TRANSITIONS in workflow_routes.py only permits these jumps from the
  // listed source status, so we expose a quick-action button only when the
  // transition is actually allowed.
  const QUOTE_TRANSITION_RULES = {
    sent_to_client: ['client_approved', 'agreement_signed'],
    client_approved: ['agreement_signed'], // already approved -> just upgrade
    approved: [], // earlier in pipeline; user must send to client first
    draft: [],
    pending_approval: [],
    on_hold: [],
    rejected: [],
  };
  const [approvalGuard, setApprovalGuard] = useState({ open: false, quote: null });
  
  // Attachment Modal State
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [selectedDocForAttachment, setSelectedDocForAttachment] = useState(null);
  const [attachmentDocType, setAttachmentDocType] = useState('invoice');

  // Mark-as-Paid dialog state — collects payment mode + bank/cash account so
  // the auto-created Receipt Voucher posts to the correct ledger account and
  // reflects in the bank statement / dashboard immediately.
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [cashBankAccounts, setCashBankAccounts] = useState([]);
  const [markPaidForm, setMarkPaidForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer', // 'cash' | 'bank_transfer' | 'cheque' | 'card' | 'online'
    account_id: '',
    cheque_number: '',
    cheque_date: '',
  });
  
  // Workflow Modal States
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowAction, setWorkflowAction] = useState(null); // 'submit', 'approve', 'reject', 'edit', 'delete'
  const [workflowDoc, setWorkflowDoc] = useState(null);
  const [workflowNote, setWorkflowNote] = useState('');
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [saveAsRevision, setSaveAsRevision] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showApprovalsQueue, setShowApprovalsQueue] = useState(false);
  // Show historical quotation revisions (R0, R1, R2…) alongside the latest.
  // When OFF (default) only the latest revision of each document tree is shown.
  // Show all revisions by default — every R0/R1/R2 listed and editable.
  // Persist the user's preference to localStorage so the choice sticks across reloads.
  // Default to "latest revisions only" so users see a clean list.
  // Toggle to reveal historic R0/R1/R2… on demand.
  const [showAllRevisions, setShowAllRevisions] = useState(() => {
    const saved = localStorage.getItem('quotations_show_all_revisions');
    return saved === null ? false : saved === 'true';
  });
  useEffect(() => {
    localStorage.setItem('quotations_show_all_revisions', String(showAllRevisions));
  }, [showAllRevisions]);

  // Quotation status filter — drives both the on-screen list and the summary
  // PDF/Excel export so what the user sees matches what they print.
  // "all" is the default (no status filter). Every other value maps 1:1 to the
  // backend `/api/summary/quotations?status=...` query param.
  const [quotationStatusFilter, setQuotationStatusFilter] = useState('all');
  // Same treatment for invoices — same filter drives DocSummary aggregate,
  // PDF/Excel export AND the on-screen table so all three stay in sync.
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  // Revision diff viewer state — opens a modal that compares any two revisions
  // of the selected quotation side-by-side.
  const [diffViewer, setDiffViewer] = useState({ open: false, quotationId: null, documentNumber: '' });
  
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    address: ''
  });
  
  const [formData, setFormData] = useState({
    customer_id: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reference: '',
    payment_terms: 'net30',
    overall_discount: 0,
    overall_discount_type: 'percentage',
    vat_category: 'standard',
    is_vat_inclusive: false,
    notes: '',
    salutation: '',
    terms_conditions: '',
    scope_of_works: '',
    exclusions: '',
    payment_terms_detail: '',
    po_reference: '',
    project_reference: '',
    project_location: '',
    quotation_id: '',
    billing_type: '',
    invoice_type: 'tax_invoice',
    layout_template: '',  // empty = inherit from company settings
    hide_audit_trail: false,
    lines: [{ description: '', specification: '', unit: '', quantity: 1, unit_price: 0, tax_rate: 5, discount: 0 }]
  });

  // Edit mode state
  const [editingDocument, setEditingDocument] = useState(null); // Holds the document being edited

  // Draft auto-save (only when creating a NEW document, not editing)
  const draft = useDraftAutosave({
    key: `sales-${createType}`,
    data: formData,
    enabled: isCreateOpen && !editingDocument,
    userId: user?.id,
  });

  const canCreate = hasPermission('sales', 'create');
  const canEdit = hasPermission('sales', 'edit');

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch when the "Show all revisions" toggle flips
  useEffect(() => {
    if (loading) return; // skip the initial mount (handled by fetchData above)
    refetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllRevisions]);

  const refetchQuotations = async () => {
    try {
      const url = showAllRevisions
        ? `${API_URL}/quotations?include_revisions=true`
        : `${API_URL}/quotations`;
      const res = await axios.get(url);
      setQuotations(res.data);
    } catch {
      toast.error('Failed to reload quotations');
    }
  };

  const fetchData = async () => {
    try {
      const quotationsUrl = showAllRevisions
        ? `${API_URL}/quotations?include_revisions=true`
        : `${API_URL}/quotations`;
      const [quotationsRes, invoicesRes, contactsRes, statsRes] = await Promise.all([
        axios.get(quotationsUrl),
        axios.get(`${API_URL}/invoices`),
        axios.get(`${API_URL}/contacts?contact_type=customer`),
        axios.get(`${API_URL}/modules/sales/stats`)
      ]);
      setQuotations(quotationsRes.data);
      setInvoices(invoicesRes.data);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = () => {
    // Default new-line VAT to the document-level VAT category: 5% only for
    // "standard"; 0 for zero-rated, exempt and out-of-scope. User can still
    // override manually after adding the line.
    const defaultRate = (formData.vat_category === 'standard') ? 5 : 0;
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', specification: '', unit: '', quantity: 1, unit_price: 0, tax_rate: defaultRate, discount: 0 }]
    }));
  };

  const handleRemoveLine = (index) => {
    if (formData.lines.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  const handleLineChange = (index, field, value) => {
    // Numeric line fields are coerced to a number; everything else (description,
    // specification, unit, etc.) must be kept as-is to preserve free text.
    const NUMERIC_FIELDS = ['quantity', 'unit_price', 'tax_rate', 'discount', 'line_progress_percent'];
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index
          ? { ...line, [field]: NUMERIC_FIELDS.includes(field) ? (parseFloat(value) || 0) : value }
          : line
      )
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let lineDiscountTotal = 0;
    let preRetentionTax = 0; // tax computed on the original (pre-retention) base

    const inclusive = !!formData.is_vat_inclusive && (formData.vat_category || 'standard') === 'standard';

    // Progress / part-billing: totals must reflect ONLY this invoice's share
    const isProgressInv = createType === 'invoice' && formData.billing_type === 'progress';
    const perLineProgress = isProgressInv && formData.progress_mode === 'per_line';
    const overallPct = isProgressInv && formData.progress_mode === 'overall'
      ? (formData.progress_percent ?? 100) : 100;
    let contractSubtotal = 0;

    formData.lines.forEach(line => {
      const fullBase = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
      contractSubtotal += fullBase;
      const linePct = perLineProgress ? Math.min(100, Math.max(0, Number(line.line_progress_percent ?? 100) || 0)) : 100;
      const lineBase = fullBase * (linePct / 100);
      const rate = (parseFloat(line.tax_rate) || 0) / 100;
      const lineDiscountValue = parseFloat(line.discount) || 0;
      const lineDiscount = lineBase * (lineDiscountValue / 100);
      const lineAfterDisc = lineBase - lineDiscount;

      if (inclusive && rate > 0) {
        const divisor = 1 + rate;
        const lineBaseNet = lineBase / divisor;
        const lineDiscountNet = lineDiscount / divisor;
        const lineTaxableNet = lineAfterDisc / divisor;
        const lineTax = lineAfterDisc - lineTaxableNet;
        subtotal += lineBaseNet;
        lineDiscountTotal += lineDiscountNet;
        preRetentionTax += lineTax;
      } else {
        const lineTax = lineAfterDisc * rate;
        subtotal += lineBase;
        lineDiscountTotal += lineDiscount;
        preRetentionTax += lineTax;
      }
    });

    // Calculate overall discount
    const afterLineDiscount = subtotal - lineDiscountTotal;
    let overallDiscount = 0;
    if (formData.overall_discount_type === 'percentage') {
      overallDiscount = afterLineDiscount * (formData.overall_discount / 100);
    } else {
      overallDiscount = formData.overall_discount;
    }

    if (overallPct > 0 && overallPct < 100) {
      const f = overallPct / 100;
      subtotal *= f;
      lineDiscountTotal *= f;
      overallDiscount *= f;
      preRetentionTax *= f;
    }

    const totalDiscount = lineDiscountTotal + overallDiscount;
    const subtotalNetOfDiscount = subtotal - totalDiscount;

    // ---- Retention is deducted BEFORE tax ----
    // Tax is then computed on (subtotal − retention), proportionally scaled
    // from the original per-line tax rates so existing tax_rate config still
    // drives the math.  This mirrors the user-supplied Ghumman sample invoice.
    const retention = parseFloat(formData.retention_amount) || 0;
    const advance = parseFloat(formData.advance_payment) || 0;
    const previous = parseFloat(formData.previous_payments) || 0;

    let taxTotal = preRetentionTax;
    let taxableAmount = subtotalNetOfDiscount;
    if (retention > 0 && subtotalNetOfDiscount > 0) {
      const scale = Math.max(0, (subtotalNetOfDiscount - retention) / subtotalNetOfDiscount);
      taxTotal = preRetentionTax * scale;
      taxableAmount = subtotalNetOfDiscount - retention;
    }

    const total = taxableAmount + taxTotal;
    const finalPayable = total - advance - previous;

    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      lineDiscountTotal: lineDiscountTotal.toFixed(2),
      overallDiscount: overallDiscount.toFixed(2),
      discountTotal: totalDiscount.toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      retention: retention.toFixed(2),
      advance: advance.toFixed(2),
      previous: previous.toFixed(2),
      total: total.toFixed(2),
      finalPayable: finalPayable.toFixed(2),
      isProgress: isProgressInv && (perLineProgress || (overallPct > 0 && overallPct < 100)),
      progressLabel: perLineProgress ? 'per-line %' : `${overallPct}%`,
      contractSubtotal: contractSubtotal.toFixed(2),
    };
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email) {
      toast.error('Customer name and email are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/contacts`, {
        ...newCustomer,
        contact_type: 'customer'
      });
      
      // Add to contacts list and select
      const createdCustomer = response.data;
      setContacts(prev => [...prev, createdCustomer]);
      setFormData(prev => ({ ...prev, customer_id: createdCustomer.id }));
      setShowNewCustomer(false);
      setNewCustomer({ name: '', email: '', phone: '', company_name: '', address: '' });
      toast.success('Customer created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    // Progress invoices may legitimately have 0 qty / 0 price rows
    const isProgress = formData.billing_type === 'progress';
    if (formData.lines.some(l => !l.description)) {
      toast.error('Each line item must have a description');
      return;
    }
    if (!isProgress && formData.lines.some(l => Number(l.unit_price) <= 0)) {
      toast.error('Please fill all line items with valid prices');
      return;
    }
    if (formData.lines.some(l => Number(l.unit_price) < 0 || Number(l.quantity) < 0)) {
      toast.error('Quantity and price cannot be negative');
      return;
    }
    // Quotation-specific validation
    if ((createType === 'quotation' || editingDocument?.docType === 'quotation') && !editingDocument) {
      if (!formData.valid_until) {
        toast.error('Quote validity date is mandatory for quotations');
        return;
      }
      if (!formData.payment_terms) {
        toast.error('Payment terms are mandatory for quotations');
        return;
      }
    }
    
    setFormLoading(true);
    try {
      const isEditing = !!editingDocument;
      const docType = isEditing ? editingDocument.docType : createType;
      const endpoint = docType === 'quotation' ? '/quotations' : '/invoices';
      
      if (isEditing) {
        // Update existing document via workflow edit endpoint
        const workflowDocType = docType === 'invoice' ? 'invoices' : 'quotations';
        const params = saveAsRevision ? '?save_as_revision=true' : '';

        // If the doc is beyond draft/rejected, backend requires a reason and
        // will auto-reset status → draft (fresh approval cycle triggered).
        const priorStatus = editingDocument.status || 'draft';
        const postApproval = priorStatus && !['draft', 'rejected'].includes(priorStatus);
        let editReason = '';
        if (postApproval && !saveAsRevision) {
          editReason = window.prompt(
            `This ${docType} is currently "${priorStatus}". Editing it will reset its status back to DRAFT and require a fresh approval.\n\nPlease enter the reason for this edit:`,
            ''
          );
          if (!editReason || editReason.trim().length < 5) {
            toast.error('Edit cancelled — reason (min 5 chars) is required for post-approval edits');
            return;
          }
        }
        const payload = { ...formData };
        if (editReason) payload.edit_after_approval_reason = editReason.trim();

        const res = await axios.put(`${API_URL}/workflow/edit/${workflowDocType}/${editingDocument.id}${params}`, payload);
        const editType = res.data?.edit_type;
        const newRev = res.data?.revision;
        if (editType === 'revision') {
          toast.success(`Saved as new revision ${newRev}`);
        } else {
          toast.success(`${docType === 'quotation' ? 'Quotation' : 'Invoice'} updated successfully${postApproval ? ' — status reset to Draft, re-approval required' : ''}`);
        }
      } else {
        // Create new document
        await axios.post(`${API_URL}${endpoint}`, formData);
        toast.success(`${createType === 'quotation' ? 'Quotation' : 'Invoice'} created successfully`);
      }
      
      setIsCreateOpen(false);
      setEditingDocument(null);
      setSaveAsRevision(false);
      resetForm();
      if (!isEditing) draft.clear();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingDocument ? 'update' : 'create'} document`);
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      date: new Date().toISOString().split('T')[0],
      due_date: '',
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reference: '',
      overall_discount: 0,
      overall_discount_type: 'percentage',
      vat_category: 'standard',
      is_vat_inclusive: false,
      notes: '',
      salutation: '',
      terms_conditions: '',
      scope_of_works: '',
      exclusions: '',
      payment_terms_detail: '',
      po_reference: '',
      project_reference: '',
      project_location: '',
      layout_template: '',
      hide_audit_trail: false,
      lines: [{ description: '', specification: '', unit: '', quantity: 1, unit_price: 0, tax_rate: 5, discount: 0 }]
    });
    setShowNewCustomer(false);
    setNewCustomer({ name: '', email: '', phone: '', company_name: '', address: '' });
    setEditingDocument(null);
    setCustomPaymentTermsMode(false);
  };

  // Fetch pending approvals (for managers/admins)
  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(`${API_URL}/workflow/pending`);
      setPendingApprovals(res.data.pending_approvals || []);
    } catch (error) {
      // Not a manager/admin or no pending approvals
      setPendingApprovals([]);
    }
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  // Open workflow modal
  const openWorkflowModal = (action, doc, docType) => {
    if (action === 'edit') {
      // For edit, open the full create form pre-populated with document data
      setEditingDocument({ ...doc, docType });
      setCreateType(docType === 'invoice' ? 'invoice' : 'quotation');
      
      // Pre-populate formData with document's full data
      setFormData({
        customer_id: doc.customer_id,
        date: doc.date,
        due_date: doc.due_date || '',
        valid_until: doc.valid_until || '',
        reference: doc.reference || '',
        overall_discount: doc.overall_discount || 0,
        overall_discount_type: doc.overall_discount_type || 'percentage',
        vat_category: doc.vat_category || 'standard',
        is_vat_inclusive: !!doc.is_vat_inclusive,
        notes: doc.notes || '',
        salutation: doc.salutation || '',
        terms_conditions: doc.terms_conditions || '',
        scope_of_works: doc.scope_of_works || '',
        exclusions: doc.exclusions || '',
        payment_terms_detail: doc.payment_terms_detail || '',
        payment_terms: doc.payment_terms || '',
        po_reference: doc.po_reference || '',
        project_reference: doc.project_reference || '',
        project_location: doc.project_location || '',
        layout_template: doc.layout_template || '',
        hide_audit_trail: !!doc.hide_audit_trail,
        ...(docType === 'invoice' ? {
          billing_type: doc.billing_type || '',
          progress_mode: doc.progress_mode || null,
          progress_percent: doc.progress_percent ?? undefined,
          invoice_type: doc.invoice_type || 'tax_invoice',
          retention_amount: doc.retention_amount || 0,
          advance_payment: doc.advance_payment || 0,
          previous_payments: doc.previous_payments || 0,
        } : {}),
        lines: doc.lines && doc.lines.length > 0 
          ? doc.lines.map(line => {
              const fallbackRate = (doc.vat_category && doc.vat_category !== 'standard') ? 0 : 5;
              const { progress_original_unit_price: origRate, ...rest } = line;
              return {
                ...rest,
                description: line.description || '',
                specification: line.specification || '',
                unit: line.unit || '',
                quantity: line.quantity ?? 0,
                // Per-line progress invoices store the billed rate; edit the contracted rate
                unit_price: origRate ?? line.unit_price ?? 0,
                tax_rate: (line.tax_rate === 0 ? 0 : (line.tax_rate || fallbackRate)),
                discount: line.discount || 0
              };
            })
          : [{ description: '', specification: '', unit: '', quantity: 1, unit_price: 0, tax_rate: ((doc.vat_category && doc.vat_category !== 'standard') ? 0 : 5), discount: 0 }]
      });
      
      // If the saved payment_terms isn't a known preset, auto-switch to custom-input mode
      const savedTerms = doc.payment_terms || '';
      setCustomPaymentTermsMode(!!savedTerms && !PAYMENT_TERMS_PRESETS.includes(savedTerms));

      setIsCreateOpen(true);
      return;
    }
    
    // For other actions (submit, approve, reject, delete), use the workflow modal
    setWorkflowAction(action);
    setWorkflowDoc({ ...doc, docType });
    setWorkflowNote('');
    setWorkflowModalOpen(true);
    
    if (action === 'edit') {
      setEditFormData({
        customer_id: doc.customer_id,
        customer_name: doc.customer_name,
        date: doc.date,
        due_date: doc.due_date,
        valid_until: doc.valid_until,
        reference: doc.reference || doc.po_reference,
        notes: doc.notes,
        terms: doc.terms_conditions,
        items: doc.items || doc.lines,
        subtotal: doc.subtotal,
        discount_type: doc.discount_type || doc.overall_discount_type,
        discount_value: doc.discount_value || doc.overall_discount,
        discount_amount: doc.discount_amount,
        vat_amount: doc.vat_amount,
        total: doc.total
      });
    }
  };

  // Submit for approval
  const handleSubmitForApproval = async () => {
    if (!workflowDoc) return;
    setWorkflowLoading(true);
    try {
      const docType = workflowDoc.docType === 'invoice' ? 'invoices' : 'quotations';
      await axios.post(`${API_URL}/workflow/submit/${docType}/${workflowDoc.id}`, {
        note: workflowNote
      });
      toast.success('Document submitted for approval');
      setWorkflowModalOpen(false);
      fetchData();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setWorkflowLoading(false);
    }
  };

  // Process approval (approve/reject)
  const handleApprovalAction = async (action) => {
    if (!workflowDoc) return;
    if (action === 'reject' && !workflowNote) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setWorkflowLoading(true);
    try {
      const docType = workflowDoc.docType === 'invoice' ? 'invoices' : 'quotations';
      await axios.post(`${API_URL}/workflow/action/${docType}/${workflowDoc.id}`, {
        action: action,
        note: workflowNote
      });
      toast.success(`Document ${action}d successfully`);
      setWorkflowModalOpen(false);
      fetchData();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} document`);
    } finally {
      setWorkflowLoading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async () => {
    if (!workflowDoc) return;
    const docType = workflowDoc.docType === 'invoice' ? 'invoices' : 'quotations';
    const isLocked = ['approved', 'paid', 'partial', 'partially_paid', 'sent_to_client', 'client_approved'].includes((workflowDoc.status || '').toLowerCase());
    // For approved / paid documents, require a strong confirmation ("DELETE")
    if (isLocked) {
      const docNum = workflowDoc.document_number || workflowDoc.id;
      const promptText = window.prompt(
        `DANGER — Delete ${workflowDoc.status?.toUpperCase()} document ${docNum}?\n\n` +
        `This document is locked. Deleting it will:\n` +
        `  • Move it to the Recycle Bin (recoverable)\n` +
        `  • VOID the linked Journal Entry (preserved for audit)\n` +
        `  • Remove the amount from Statement of Account and Aging\n\n` +
        `Type DELETE to confirm:`
      );
      if ((promptText || '').trim().toUpperCase() !== 'DELETE') {
        if (promptText !== null) toast.info('Delete cancelled.');
        return;
      }
    }
    setWorkflowLoading(true);
    try {
      await axios.delete(`${API_URL}/workflow/${docType}/${workflowDoc.id}`, {
        params: { reason: workflowNote, force: isLocked ? true : undefined }
      });
      toast.success(isLocked ? 'Approved document deleted and JE voided' : 'Document deleted');
      setWorkflowModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setWorkflowLoading(false);
    }
  };

  // Status transition (sent_to_client, client_approved, on_hold, closed)
  const handleTransition = async (doc, docType, newStatus) => {
    try {
      const collType = docType === 'invoice' ? 'invoices' : 'quotations';
      await axios.post(`${API_URL}/workflow/transition/${collType}/${doc.id}/${newStatus}`, {});
      toast.success(`Status changed to ${(statusLabels[newStatus] || newStatus)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Status change failed');
    }
  };

  // Duplicate quotation (creates a fresh draft with new ref no.)
  const handleDuplicateQuotation = async (quote) => {
    try {
      const res = await axios.post(`${API_URL}/quotations/${quote.id}/duplicate`, {});
      toast.success(`Duplicated as ${res.data.document_number}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate quotation');
    }
  };

  // Duplicate invoice (creates a fresh draft with new ref no.)
  const handleDuplicateInvoice = async (invoice) => {
    try {
      const res = await axios.post(`${API_URL}/invoices/${invoice.id}/duplicate`, {});
      toast.success(`Duplicated as ${res.data.document_number}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate invoice');
    }
  };


  // ---- Quotation -> Invoice helpers ----------------------------------------
  // Prefill the (still-open) Create Invoice form from the chosen quotation.
  const applyQuotationToInvoice = (q) => {
    setFormData(prev => ({
      ...prev,
      quotation_id: q.id,
      customer_id: q.customer_id,
      reference: q.reference || prev.reference,
      project_reference: q.project_reference || prev.project_reference,
      project_location: q.project_location || prev.project_location,
      notes: q.notes || prev.notes,
      terms_conditions: q.terms_conditions || prev.terms_conditions,
      lines: q.lines?.length
        ? q.lines.map(l => {
            const fallbackRate = (q.vat_category && q.vat_category !== 'standard') ? 0 : 5;
            return {
              description: l.description || '',
              specification: l.specification || '',
              unit: l.unit || '',
              quantity: l.quantity ?? 0,
              unit_price: l.unit_price ?? 0,
              tax_rate: (l.tax_rate === 0 ? 0 : (l.tax_rate || fallbackRate)),
              discount: l.discount || 0,
            };
          })
        : prev.lines,
    }));
  };

  // Move a quote to an "approved" status (client_approved / agreement_signed)
  // before the user proceeds to raise an invoice from it.  Returns the updated
  // quotation row from /quotations on success, or null on failure.
  const transitionQuoteStatus = async (quote, newStatus) => {
    try {
      await axios.post(
        `${API_URL}/workflow/transition/quotations/${quote.id}/${newStatus}`,
        {}
      );
      toast.success(`Quote ${quote.document_number} marked as ${newStatus.replace(/_/g, ' ')}`);
      // refresh local list so the dropdown's status badge updates
      const refreshed = await axios.get(`${API_URL}/quotations`);
      setQuotations(refreshed.data || []);
      return (refreshed.data || []).find(x => x.id === quote.id) || { ...quote, status: newStatus };
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to update quote status';
      toast.error(detail);
      return null;
    }
  };

  // Edit document
  const handleEditDocument = async () => {
    if (!workflowDoc || !editFormData) return;
    setWorkflowLoading(true);
    try {
      const docType = workflowDoc.docType === 'invoice' ? 'invoices' : 'quotations';
      await axios.put(`${API_URL}/workflow/edit/${docType}/${workflowDoc.id}`, editFormData);
      toast.success('Document updated successfully');
      setWorkflowModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleApprove = async (type, id) => {
    try {
      const endpoint = type === 'quotation' ? `/quotations/${id}/approve` : `/invoices/${id}/approve`;
      await axios.post(`${API_URL}${endpoint}`);
      toast.success(`${type === 'quotation' ? 'Quotation' : 'Invoice'} approved`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  // Conversion preview state — populated by /conversion-preview, then confirmed
  // in the ConvertToInvoiceDialog. Prevents duplicate vouchers by re-linking
  // any advance receipt already booked against the quotation.
  const [convertDialog, setConvertDialog] = useState({ open: false, loading: false, preview: null, dueDate: '' });

  // "Link Existing Invoice" dialog — for old quotations where an invoice was
  // raised manually before the "Convert to Invoice" flow existed. Lets the
  // user retro-attach any unlinked invoice for the same customer.
  const [linkInvoiceDialog, setLinkInvoiceDialog] = useState({ open: false, quotation: null, selectedInvoiceId: '' });

  const openLinkInvoiceDialog = (quotation) => {
    setLinkInvoiceDialog({ open: true, quotation, selectedInvoiceId: '' });
  };

  const confirmLinkInvoice = async () => {
    const { quotation, selectedInvoiceId } = linkInvoiceDialog;
    if (!quotation || !selectedInvoiceId) { toast.error('Pick an invoice first'); return; }
    try {
      const res = await axios.post(
        `${API_URL}/quotations/${quotation.id}/link-invoice?invoice_id=${selectedInvoiceId}`
      );
      const relinked = res.data?.vouchers_relinked || 0;
      toast.success(
        `Linked ${res.data.invoice_number} ↔ ${res.data.quotation_number}` +
        (relinked > 0 ? ` — ${relinked} voucher(s) re-linked.` : '')
      );
      setLinkInvoiceDialog({ open: false, quotation: null, selectedInvoiceId: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link invoice');
    }
  };

  const handleUnlinkQuotation = async (invoice) => {
    const qId = invoice.quotation_id || invoice.linked_quotation_id;
    const src = (quotations || []).find(q => q.id === qId);
    const qLabel = src?.document_number || 'the linked quotation';
    if (!window.confirm(`Unlink ${invoice.document_number} from ${qLabel}?\n\nThe invoice stays; only the quotation ↔ invoice reference is removed on both sides. Any receipt vouchers keep pointing at this invoice.`)) return;
    try {
      const res = await axios.post(`${API_URL}/invoices/${invoice.id}/unlink-quotation`);
      toast.success(`${res.data.invoice_number} unlinked${res.data.quotation_number ? ` from ${res.data.quotation_number}` : ''}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlink');
    }
  };

  const openConvertDialog = async (quotationId) => {
    setConvertDialog({
      open: true, loading: true, preview: null,
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    try {
      const res = await axios.get(`${API_URL}/quotations/${quotationId}/conversion-preview`);
      setConvertDialog(c => ({ ...c, loading: false, preview: res.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load conversion preview');
      setConvertDialog({ open: false, loading: false, preview: null, dueDate: '' });
    }
  };

  const confirmConvertToInvoice = async () => {
    const { preview, dueDate } = convertDialog;
    if (!preview) return;
    if (!dueDate) { toast.error('Please pick a due date'); return; }
    try {
      await axios.post(`${API_URL}/quotations/${preview.quotation_id}/convert-to-invoice?due_date=${dueDate}`);
      const advMsg = preview.advance_paid > 0
        ? ` Advance ${preview.advance_paid.toFixed(2)} re-linked — no duplicate voucher needed.`
        : '';
      toast.success('Quotation converted to invoice.' + advMsg);
      setConvertDialog({ open: false, loading: false, preview: null, dueDate: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  // Legacy entry point — kept in case any old caller uses it. Now just opens
  // the preview dialog instead of firing off a prompt().
  const handleConvertToInvoice = (quotationId) => openConvertDialog(quotationId);

  // ---------- Part-Invoice dialog (bill a quotation in slices) ----------
  // `preview` holds the /api/part-invoices/preview response — line balances,
  // history of earlier part invoices, advance pool. `form` holds the editable
  // per-line % + apply-mode + optional line edits before POSTing.
  const [partInvDialog, setPartInvDialog] = useState({
    open: false,
    loading: false,
    submitting: false,
    preview: null,
    form: null,   // { dueDate, advance, notes, terms, lines: [...] }
  });

  // ---- Payment Ledger — consolidated view of every invoice + receipt
  // raised against a Quotation. Answers the user's ask: "where can I
  // see amount received / balance / pending for a quote that was
  // converted to a regular invoice AND has part invoices?"
  const [ledgerDialog, setLedgerDialog] = useState({ open: false, loading: false, data: null });
  const openLedger = async (quotationId) => {
    setLedgerDialog({ open: true, loading: true, data: null });
    try {
      const res = await axios.get(`${API_URL}/part-invoices/ledger/${quotationId}`);
      setLedgerDialog({ open: true, loading: false, data: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment ledger');
      setLedgerDialog({ open: false, loading: false, data: null });
    }
  };

  const openPartInvoiceDialog = async (quotationId) => {
    setPartInvDialog({ open: true, loading: true, submitting: false, preview: null, form: null });
    try {
      const res = await axios.get(`${API_URL}/part-invoices/preview/${quotationId}`);
      const p = res.data;
      const lines = (p.line_progress || []).map((lp) => ({
        quotation_line_index: lp.index,
        description: lp.description,
        quantity: lp.quantity,
        unit_price: lp.unit_price,
        tax_rate: lp.tax_rate || 0,
        discount: lp.discount || 0,
        unit: lp.unit || '',
        specification: lp.specification || '',
        hsn_code: lp.hsn_code || '',
        sac_code: lp.sac_code || '',
        // Default suggestion = whatever balance remains on this line
        applied_percentage: lp.balance_percentage,
        apply_mode: 'amount',
        // read-only metadata used by the UI
        _already: lp.already_invoiced_percentage,
        _balance: lp.balance_percentage,
        _from_quote: true,
      }));
      setPartInvDialog({
        open: true, loading: false, submitting: false, preview: p,
        form: {
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          advance: 0,
          notes: '',
          terms: '',
          lines,
          // ---- Feb-2026: invoice type + billing mode ----
          // Default the type from the source Quotation so a proforma-
          // without-tax quote produces a proforma-without-tax part
          // invoice out of the gate. User can override in the dialog.
          invoiceType: (p.quotation_invoice_type
            || (p.quotation_vat_category === 'exempt' ? 'proforma_without_tax' : 'tax_invoice')),
          // 'per_line' = current per-line % table.
          // 'lumpsum'  = single arbitrary amount for this round.
          billingMode: 'per_line',
          lumpsumAmount: '',
          lumpsumDescription: '',
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load part-invoice preview');
      setPartInvDialog({ open: false, loading: false, submitting: false, preview: null, form: null });
    }
  };

  const updatePartInvLine = (idx, patch) => {
    setPartInvDialog((s) => {
      if (!s.form) return s;
      const nextLines = s.form.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return { ...s, form: { ...s.form, lines: nextLines } };
    });
  };

  const addPartInvExtraLine = () => {
    setPartInvDialog((s) => {
      if (!s.form) return s;
      return {
        ...s,
        form: {
          ...s.form,
          lines: [
            ...s.form.lines,
            {
              quotation_line_index: null,
              description: '',
              quantity: 1,
              unit_price: 0,
              tax_rate: s.preview?.quotation_vat_category === 'standard' ? 5 : 0,
              discount: 0,
              unit: '',
              specification: '',
              hsn_code: '',
              sac_code: '',
              applied_percentage: 100,
              apply_mode: 'amount',
              _already: 0,
              _balance: 100,
              _from_quote: false,
            },
          ],
        },
      };
    });
  };

  const removePartInvLine = (idx) => {
    setPartInvDialog((s) => {
      if (!s.form) return s;
      return { ...s, form: { ...s.form, lines: s.form.lines.filter((_, i) => i !== idx) } };
    });
  };

  // Live preview of this round's totals (before hitting the backend). Uses the
  // same math order the backend uses so the number the user sees matches what
  // they get back after POSTing.  Respects the new invoice-type (proforma-
  // without-tax zeroes the tax column) and the "lumpsum" billing mode.
  const partInvComputedTotals = React.useMemo(() => {
    const form = partInvDialog.form;
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    const noTax = form.invoiceType === 'proforma_without_tax';

    if (form.billingMode === 'lumpsum') {
      const ls = Math.max(0, parseFloat(form.lumpsumAmount) || 0);
      return { subtotal: ls, tax: 0, total: ls };
    }

    let sub = 0, tax = 0;
    for (const l of form.lines) {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const pct = Math.max(0, Math.min(parseFloat(l.applied_percentage) || 0, 100)) / 100;
      const rate = noTax ? 0 : ((parseFloat(l.tax_rate) || 0) / 100);
      let base;
      if (l.apply_mode === 'quantity') base = qty * pct * price;
      else base = qty * price * pct;
      sub += base;
      tax += base * rate;
    }
    return { subtotal: sub, tax, total: sub + tax };
  }, [partInvDialog.form]);

  const submitPartInvoice = async () => {
    const { preview, form } = partInvDialog;
    if (!preview || !form) return;
    if (!form.dueDate) { toast.error('Pick a due date'); return; }

    const isLumpsum = form.billingMode === 'lumpsum';
    const lumpsum = Math.max(0, parseFloat(form.lumpsumAmount) || 0);

    if (isLumpsum) {
      if (lumpsum <= 0) { toast.error('Enter a lumpsum amount greater than 0'); return; }
    } else {
      if (!form.lines?.length) { toast.error('Add at least one line'); return; }
      // Client-side validation for cumulative %
      for (const l of form.lines) {
        if (l._from_quote) {
          const pct = parseFloat(l.applied_percentage) || 0;
          if (pct < 0 || pct > l._balance + 1e-3) {
            toast.error(`"${l.description}" – ${pct}% exceeds balance ${l._balance}%`);
            return;
          }
        }
      }
    }

    // Skip lines with 0 % — "not billed this round" is a valid intent
    const linesToSubmit = isLumpsum
      ? form.lines  // backend replaces with synthetic line; send originals for allocation tracking
      : form.lines.filter((l) => (parseFloat(l.applied_percentage) || 0) > 0);

    if (!isLumpsum && linesToSubmit.length === 0) {
      toast.error('Enter an Approved % on at least one line');
      return;
    }
    const skipped = isLumpsum ? 0 : (form.lines.length - linesToSubmit.length);

    const advance = parseFloat(form.advance) || 0;
    if (advance > (preview.advance_available || 0) + 1e-3) {
      toast.error(`Advance ${advance} exceeds available pool ${preview.advance_available}`);
      return;
    }

    setPartInvDialog((s) => ({ ...s, submitting: true }));
    try {
      // ---- Feb-2026: invoice-type & lumpsum wiring ----
      const invoiceType = form.invoiceType || 'tax_invoice';
      const noTax = invoiceType === 'proforma_without_tax';
      const body = {
        due_date: form.dueDate,
        advance_payment: advance,
        notes: form.notes || undefined,
        terms_conditions: form.terms || undefined,
        // Force exempt when Proforma-Without-Tax so backend & PDF agree.
        vat_category: noTax ? 'exempt' : (preview.quotation_vat_category || 'standard'),
        is_vat_inclusive: !noTax && !!preview.quotation_is_vat_inclusive,
        project_reference: preview.quotation_project_reference || undefined,
        project_location: preview.quotation_project_location || undefined,
        invoice_type: invoiceType,
        lumpsum_amount: isLumpsum ? lumpsum : 0,
        lumpsum_description: isLumpsum ? (form.lumpsumDescription || undefined) : undefined,
        lines: linesToSubmit.map((l) => ({
          quotation_line_index: l._from_quote ? l.quotation_line_index : null,
          description: l.description,
          quantity: parseFloat(l.quantity) || 0,
          unit_price: parseFloat(l.unit_price) || 0,
          // Zero-out per-line tax when Proforma-Without-Tax is selected
          tax_rate: noTax ? 0 : (parseFloat(l.tax_rate) || 0),
          discount: parseFloat(l.discount) || 0,
          unit: l.unit || undefined,
          specification: l.specification || undefined,
          hsn_code: l.hsn_code || undefined,
          sac_code: l.sac_code || undefined,
          applied_percentage: Math.max(0, Math.min(parseFloat(l.applied_percentage) || 0, 100)),
          apply_mode: l.apply_mode || 'amount',
        })),
      };
      const res = await axios.post(`${API_URL}/part-invoices/${preview.quotation_id}`, body);
      const skippedNote = skipped > 0 ? ` — ${skipped} zero-% line${skipped > 1 ? 's' : ''} skipped` : '';
      toast.success(
        `Part invoice ${res.data.document_number} created${skippedNote}` +
        (res.data.is_fully_invoiced ? ' — quotation is now fully invoiced.' : '')
      );
      setPartInvDialog({ open: false, loading: false, submitting: false, preview: null, form: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create part invoice');
      setPartInvDialog((s) => ({ ...s, submitting: false }));
    }
  };

  // ---------- Project Part-Invoice dialog (invoice-driven flow, no quotation) ----------
  // Chain is identified by (customer_id + project_reference). Round 1 lets
  // the user manually enter Original Qty/Rate + Approved Qty. Round N loads
  // anchor lines from the preview and enforces cumulative-qty ≤ base_scope.
  const [projPartDialog, setProjPartDialog] = useState({
    open: false,
    step: 'setup',    // 'setup' → 'form' → 'submitting'
    loading: false,
    submitting: false,
    customerId: '',
    projectReference: '',
    preview: null,
    form: null,       // { dueDate, advance, notes, terms, lines: [...] }
  });

  // Auto-save the in-progress form to localStorage so an accidental page
  // close / reload / navigation doesn't destroy typed work. Keyed per user
  // (not per project) — the setup step surfaces a Restore banner if a draft
  // is found, letting the accountant continue whatever they were working on.
  const projPartDraftPayload = React.useMemo(() => ({
    customerId: projPartDialog.customerId,
    projectReference: projPartDialog.projectReference,
    step: projPartDialog.step,
    // We deliberately DON'T persist `preview` — it's re-fetched from the
    // server on restore so cumulative-qty / balance figures stay accurate.
    form: projPartDialog.form,
  }), [projPartDialog.customerId, projPartDialog.projectReference,
        projPartDialog.step, projPartDialog.form]);

  const projPartDraft = useDraftAutosave({
    key: 'sales-part-invoice-project',
    data: projPartDraftPayload,
    // Enabled whenever the dialog is open — the hook itself skips saving
    // until `_hasContent` returns true, so gating here on customer/project
    // would erase the Restore banner when the user reopens with an empty
    // setup step.
    enabled: projPartDialog.open,
    userId: user?.id,
  });

  // Warn on tab-close if the user has unsaved content — mirrors the
  // browser's native "you have unsaved changes" prompt so accidental
  // navigation is caught before it wipes the form. useDraftAutosave already
  // does one final flush on `beforeunload`, but the confirm() ensures the
  // user actually acknowledges the risk.
  useEffect(() => {
    if (!projPartDialog.open) return;
    const dirty = !!projPartDialog.customerId ||
                  !!(projPartDialog.projectReference || '').trim() ||
                  !!(projPartDialog.form?.lines?.some(l => Number(l.approved_quantity) > 0 || (l.description || '').trim() !== ''));
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required for the prompt to show in modern browsers
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [projPartDialog.open, projPartDialog.customerId,
      projPartDialog.projectReference, projPartDialog.form]);

  const openProjPartDialog = () => {
    setProjPartDialog({
      open: true, step: 'setup', loading: false, submitting: false,
      customerId: '', projectReference: '',
      preview: null, form: null,
    });
  };

  // Restore the last-saved draft when the user clicks the banner. We push
  // the caller back into the FORM step and refresh the preview so anchor
  // balances reflect the server's current state (protects against another
  // user having raised a part invoice in the same chain in the meantime).
  const restoreProjPartDraft = async () => {
    const saved = projPartDraft.restore();
    if (!saved) return;
    setProjPartDialog((s) => ({
      ...s,
      customerId: saved.customerId || '',
      projectReference: saved.projectReference || '',
      form: saved.form || null,
      // If the draft was captured mid-form, reload the preview from the
      // server before showing the form so anchor scope reflects any
      // concurrent changes.
      step: saved.step === 'form' ? 'setup' : (saved.step || 'setup'),
    }));
    if (saved.step === 'form' && saved.customerId && (saved.projectReference || '').trim()) {
      try {
        const res = await axios.get(`${API_URL}/part-invoices/project/preview`, {
          params: { customer_id: saved.customerId, project_reference: saved.projectReference.trim() },
        });
        setProjPartDialog((s) => ({ ...s, step: 'form', preview: res.data, form: saved.form }));
      } catch (e) {
        toast.error('Draft restored — please click Continue to reload project scope');
      }
    }
  };


  const loadProjPartPreview = async () => {
    const { customerId, projectReference } = projPartDialog;
    if (!customerId) { toast.error('Pick a customer'); return; }
    if (!projectReference?.trim()) { toast.error('Enter a project reference'); return; }
    setProjPartDialog((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(`${API_URL}/part-invoices/project/preview`, {
        params: { customer_id: customerId, project_reference: projectReference.trim() },
      });
      const p = res.data;
      // Build the initial form. Round-1 → one empty line. Round-N → hydrate
      // from anchor progress (locked base + editable approved qty).
      let lines;
      if (p.is_first_round) {
        lines = [{
          _from_chain: false,
          base_line_key: null,
          description: '',
          original_quantity: 0,
          original_unit_price: 0,
          approved_quantity: 0,
          approved_percentage: 0,
          tax_rate: 5,
          unit: '',
          specification: '',
          hsn_code: '',
          sac_code: '',
          _anchor_original_quantity: 0,
          _anchor_original_unit_price: 0,
          _already_invoiced_quantity: 0,
          _balance_quantity: 0,
        }];
      } else {
        lines = (p.line_progress || []).map((lp) => ({
          _from_chain: true,
          base_line_key: lp.base_line_key,
          description: lp.description,
          original_quantity: lp.original_quantity,
          original_unit_price: lp.original_unit_price,
          approved_quantity: 0,
          approved_percentage: 0,
          tax_rate: lp.tax_rate || 0,
          unit: lp.unit || '',
          specification: lp.specification || '',
          hsn_code: lp.hsn_code || '',
          sac_code: lp.sac_code || '',
          _anchor_original_quantity: lp.original_quantity,
          _anchor_original_unit_price: lp.original_unit_price,
          _already_invoiced_quantity: lp.already_invoiced_quantity,
          _balance_quantity: lp.balance_quantity,
        }));
      }
      setProjPartDialog((s) => ({
        ...s,
        step: 'form',
        loading: false,
        preview: p,
        form: {
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          advance: 0,
          notes: '',
          terms: '',
          projectLocation: '',
          lines,
        },
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
      setProjPartDialog((s) => ({ ...s, loading: false }));
    }
  };

  const updateProjPartLine = (idx, patch) => {
    setProjPartDialog((s) => {
      if (!s.form) return s;
      const nextLines = s.form.lines.map((l, i) => {
        if (i !== idx) return l;
        const merged = { ...l, ...patch };
        // Keep approved qty ↔ approved % in sync — regardless of order-of-entry.
        // Rule: whichever field the user JUST touched wins; the other one
        // (and cascades from original_quantity) recomputes from it.
        const origQty = parseFloat(merged.original_quantity) || 0;
        if (Object.prototype.hasOwnProperty.call(patch, 'approved_quantity')) {
          const q = parseFloat(merged.approved_quantity) || 0;
          merged.approved_percentage = origQty > 0
            ? Math.round((q / origQty) * 10000) / 100
            : 0;
        } else if (Object.prototype.hasOwnProperty.call(patch, 'approved_percentage')) {
          const pct = Math.max(0, Math.min(parseFloat(merged.approved_percentage) || 0, 100));
          merged.approved_quantity = origQty > 0
            ? Math.round(origQty * pct) / 100
            : 0;
        } else if (Object.prototype.hasOwnProperty.call(patch, 'original_quantity')) {
          // Original Qty changed AFTER the user already typed something. If
          // they entered a % first (qty is still 0 but pct > 0) → recompute
          // qty from the new original. Otherwise keep qty and recompute pct.
          const curPct = parseFloat(merged.approved_percentage) || 0;
          const curQty = parseFloat(merged.approved_quantity) || 0;
          if (curPct > 0 && curQty <= 0) {
            merged.approved_quantity = origQty > 0
              ? Math.round(origQty * Math.min(curPct, 100)) / 100
              : 0;
          } else {
            merged.approved_percentage = origQty > 0
              ? Math.round((curQty / origQty) * 10000) / 100
              : 0;
          }
        }
        return merged;
      });
      return { ...s, form: { ...s.form, lines: nextLines } };
    });
  };

  const addProjPartExtraLine = () => {
    setProjPartDialog((s) => {
      if (!s.form) return s;
      return {
        ...s,
        form: {
          ...s.form,
          lines: [
            ...s.form.lines,
            {
              _from_chain: false,
              base_line_key: null,
              description: '',
              original_quantity: 0,
              original_unit_price: 0,
              approved_quantity: 0,
              approved_percentage: 0,
              tax_rate: 5,
              unit: '',
              specification: '',
              hsn_code: '',
              sac_code: '',
              _anchor_original_quantity: 0,
              _anchor_original_unit_price: 0,
              _already_invoiced_quantity: 0,
              _balance_quantity: 0,
            },
          ],
        },
      };
    });
  };

  const removeProjPartLine = (idx) => {
    setProjPartDialog((s) => {
      if (!s.form) return s;
      return { ...s, form: { ...s.form, lines: s.form.lines.filter((_, i) => i !== idx) } };
    });
  };

  const projPartTotals = React.useMemo(() => {
    const form = projPartDialog.form;
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    let sub = 0, tax = 0;
    for (const l of form.lines) {
      const qty = parseFloat(l.approved_quantity) || 0;
      const price = parseFloat(l.original_unit_price) || 0;
      const rate = (parseFloat(l.tax_rate) || 0) / 100;
      const base = qty * price;
      sub += base;
      tax += base * rate;
    }
    return { subtotal: sub, tax, total: sub + tax };
  }, [projPartDialog.form]);

  const submitProjPart = async () => {
    const { preview, form, customerId, projectReference } = projPartDialog;
    if (!preview || !form) return;
    if (!form.dueDate) { toast.error('Pick a due date'); return; }
    if (!form.lines?.length) { toast.error('Add at least one line'); return; }

    // Auto-derive approved_quantity from approved_percentage if the user
    // typed only the % (common shortcut). Also filter out lines where the
    // user has explicitly left approved_qty at 0 — those lines are simply
    // "not billed this round" rather than a validation error.
    const linesToSubmit = form.lines
      .map((l) => {
        const origQty = parseFloat(l.original_quantity) || 0;
        let approved = parseFloat(l.approved_quantity) || 0;
        const pct = parseFloat(l.approved_percentage) || 0;
        // Safety net: user typed % but qty didn't sync (e.g. % entered
        // before original qty, then original qty typed later)
        if (approved <= 0 && pct > 0 && origQty > 0) {
          approved = Math.round(origQty * Math.min(pct, 100)) / 100;
        }
        return { ...l, approved_quantity: approved };
      })
      .filter((l) => (parseFloat(l.approved_quantity) || 0) > 0);

    if (linesToSubmit.length === 0) {
      toast.error('Enter an Approved Qty (or Approved %) on at least one line');
      return;
    }

    // Per-line ceilings — cumulative already-invoiced + this round ≤ scope
    for (const l of linesToSubmit) {
      const approved = parseFloat(l.approved_quantity) || 0;
      if (l._from_chain && approved > l._balance_quantity + 1e-3) {
        toast.error(`"${l.description}" — approved qty ${approved} exceeds balance ${l._balance_quantity}`);
        return;
      }
      if (!l._from_chain) {
        const orig = parseFloat(l.original_quantity) || 0;
        if (approved > orig + 1e-3) {
          toast.error(`"${l.description || 'Line'}" — approved qty ${approved} exceeds original qty ${orig}`);
          return;
        }
      }
    }

    const skipped = form.lines.length - linesToSubmit.length;

    setProjPartDialog((s) => ({ ...s, submitting: true }));
    try {
      const body = {
        customer_id: customerId,
        project_reference: projectReference.trim(),
        project_location: form.projectLocation || undefined,
        due_date: form.dueDate,
        advance_payment: parseFloat(form.advance) || 0,
        notes: form.notes || undefined,
        terms_conditions: form.terms || undefined,
        vat_category: 'standard',
        is_vat_inclusive: false,
        lines: linesToSubmit.map((l) => ({
          base_line_key: l.base_line_key || null,
          description: l.description,
          original_quantity: parseFloat(l.original_quantity) || 0,
          original_unit_price: parseFloat(l.original_unit_price) || 0,
          approved_quantity: parseFloat(l.approved_quantity) || 0,
          tax_rate: parseFloat(l.tax_rate) || 0,
          unit: l.unit || undefined,
          specification: l.specification || undefined,
          hsn_code: l.hsn_code || undefined,
          sac_code: l.sac_code || undefined,
        })),
      };
      const res = await axios.post(`${API_URL}/part-invoices/project/create`, body);
      const dev = res.data.deviation_notes?.length ? ` (${res.data.deviation_notes.length} audit note${res.data.deviation_notes.length > 1 ? 's' : ''})` : '';
      const skippedNote = skipped > 0 ? ` — ${skipped} zero-qty line${skipped > 1 ? 's' : ''} skipped` : '';
      toast.success(
        `Part invoice ${res.data.document_number} created${dev}${skippedNote}` +
        (res.data.is_fully_invoiced ? '. Project is now fully invoiced.' : '')
      );
      setProjPartDialog({ open: false, step: 'setup', loading: false, submitting: false, customerId: '', projectReference: '', preview: null, form: null });
      projPartDraft.clear();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create part invoice');
      setProjPartDialog((s) => ({ ...s, submitting: false }));
    }
  };


  const handleMarkPaid = async (invoiceId) => {
    // Open the Mark-as-Paid dialog. Fetch cash/bank accounts (codes 1000-1099)
    // so the user can pick which account the payment goes into.
    const invoice = invoices.find(i => i.id === invoiceId);
    if (!invoice) return;
    setMarkPaidInvoice(invoice);
    try {
      const accountsRes = await axios.get(`${API_URL}/accounts`);
      const list = (accountsRes.data || []).filter(a => {
        const code = String(a.code || '');
        // Cash (1000-1099) + Bank (1100-1199 except 1100 AR + 1200-1299)
        return code.startsWith('10') || (a.account_type || '').toLowerCase().includes('cash') || (a.account_type || '').toLowerCase().includes('bank');
      });
      setCashBankAccounts(list);
      // Default to the first bank account, fall back to cash
      const defaultAcc = list.find(a => String(a.code || '').startsWith('10') && String(a.code || '') !== '1000') || list.find(a => String(a.code || '') === '1000') || list[0];
      setMarkPaidForm({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: defaultAcc && String(defaultAcc.code || '') === '1000' ? 'cash' : 'bank_transfer',
        account_id: defaultAcc?.id || '',
        cheque_number: '',
        cheque_date: '',
        payment_type: 'final',
        // Default to the full remaining balance
        settlement_amount: Math.max(
          0,
          invoiceBilled(invoice) - (invoice.amount_paid || 0)
        ).toFixed(2),
      });
    } catch (e) {
      // If accounts fetch fails, dialog still opens — the backend will fall
      // back to default cash (code 1000) if no account_id is sent.
      setCashBankAccounts([]);
    }
    setMarkPaidOpen(true);
  };

  const confirmMarkPaid = async () => {
    if (!markPaidInvoice) return;
    setMarkPaidLoading(true);
    try {
      const params = new URLSearchParams();
      if (markPaidForm.payment_date) params.set('payment_date', markPaidForm.payment_date);
      if (markPaidForm.payment_method) params.set('payment_method', markPaidForm.payment_method);
      params.set('payment_type', markPaidForm.payment_type || 'final');
      if ((markPaidForm.payment_type || 'final') === 'partial') {
        const amt = parseFloat(markPaidForm.settlement_amount || '0');
        if (!amt || amt <= 0) {
          toast.error('Please enter a valid part-payment amount greater than 0');
          setMarkPaidLoading(false);
          return;
        }
        params.set('settlement_amount', String(amt));
      }
      if (markPaidForm.cheque_number) params.set('cheque_number', markPaidForm.cheque_number);
      if (markPaidForm.cheque_date) params.set('cheque_date', markPaidForm.cheque_date);
      // Decide whether the chosen account is a cash or bank account
      const chosen = cashBankAccounts.find(a => a.id === markPaidForm.account_id);
      const isCash = chosen && (String(chosen.code || '') === '1000' || (chosen.account_type || '').toLowerCase() === 'cash');
      if (markPaidForm.account_id) {
        if (isCash) {
          params.set('cash_account_id', markPaidForm.account_id);
        } else {
          params.set('bank_account_id', markPaidForm.account_id);
        }
      }
      const resp = await axios.post(`${API_URL}/invoices/${markPaidInvoice.id}/mark-paid?${params.toString()}`);
      const methodLabel = {
        cash: 'Cash',
        bank_transfer: 'Bank Transfer',
        cheque: 'Cheque',
        card: 'Card',
        online: 'Online / UPI',
      }[markPaidForm.payment_method] || 'Payment';
      const acctLabel = chosen ? `${chosen.code} · ${chosen.name}` : 'the default account';
      const isPartial = resp?.data?.payment_type === 'partial';
      toast.success(
        isPartial
          ? `Part payment recorded (${formatCurrency(resp.data.settled_amount)}) — balance ${formatCurrency(resp.data.new_balance_due)} remaining. ${methodLabel} posted to ${acctLabel}.`
          : `Invoice marked as paid — ${methodLabel} posted to ${acctLabel}`
      );
      setMarkPaidOpen(false);
      setMarkPaidInvoice(null);
      fetchData();
    } catch (error) {
      const raw = error?.response?.data?.detail;
      let msg = 'Failed to mark as paid';
      if (typeof raw === 'string') msg = raw;
      else if (raw && typeof raw === 'object') msg = raw.message || raw.code || JSON.stringify(raw);
      else if (error?.message) msg = error.message;
      toast.error(msg);
    } finally {
      setMarkPaidLoading(false);
    }
  };

  // PDF Preview Handler
  const handlePreviewPdf = async (docId, docNumber, docType = 'invoice') => {
    try {
      const endpoint = docType === 'invoice' ? 'invoices' : 'quotations';
      const response = await axios.get(`${API_URL}/${endpoint}/${docId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfPreviewUrl(url);
      setPdfPreviewTitle(`${docType === 'invoice' ? 'Invoice' : 'Quotation'} ${docNumber}`);
      setPdfPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF preview');
    }
  };

  const handleDownloadPdf = async (invoiceId, docNumber) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${docNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadQuotationPdf = async (quotationId, docNumber) => {
    try {
      const response = await axios.get(`${API_URL}/quotations/${quotationId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${docNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Attachment Handlers
  const openAttachmentModal = (doc, docType) => {
    setSelectedDocForAttachment({ ...doc, docType });
    setAttachmentDocType(docType);
    setAttachmentModalOpen(true);
  };

  const totals = calculateTotals();
  const showPerLineProgress = createType === 'invoice' && formData.billing_type === 'progress' && formData.progress_mode === 'per_line';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="sales-page">
      {/* Client Context Banner for Accountant Mode */}
      <ClientContextBanner />
      
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Sales
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage quotations, invoices, and track sales
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/contacts">
            <Button variant="outline" data-testid="contacts-btn">
              <Users size={18} className="mr-2" />
              Contacts
            </Button>
          </Link>
          {canCreate && (
            <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) { resetForm(); setSaveAsRevision(false); setEditingDocument(null); } }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-document-btn"
                  onClick={() => {
                    // Pre-select the document type to match the visible tab so a
                    // user clicking "Create" while on Quotations doesn't end up
                    // creating an Invoice (and vice versa). The toggle inside the
                    // modal still allows switching.
                    setCreateType(activeTab === 'quotations' ? 'quotation' : 'invoice');
                  }}
                >
                  <Plus size={18} className="mr-2" />
                  Create
                </Button>
              </DialogTrigger>
              <DialogContent
                className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-5xl sm:min-w-[900px] max-h-[90vh] overflow-x-auto overflow-y-auto p-4 sm:p-6"
                style={{
                  // Dragging the bottom-right corner extends both width & height
                  // on desktop. Disabled on touch / small screens because the
                  // hard `minWidth: 640px` was making the modal overflow on
                  // phones (right side cut off, can't see Save button).
                  resize: 'both',
                  minHeight: '420px',
                }}
                data-testid="create-doc-dialog"
              >
                <DialogHeader>
                  <DialogTitle>
                    {editingDocument 
                      ? `Edit ${editingDocument.docType === 'quotation' ? 'Quotation' : 'Invoice'} - ${editingDocument.document_number}`
                      : `Create ${createType === 'quotation' ? 'Quotation' : 'Invoice'}`
                    }
                  </DialogTitle>
                  <DialogDescription>
                    {editingDocument ? 'Update the document details below' : 'Fill in the details below'}
                  </DialogDescription>
                </DialogHeader>
                {/* Show which company this document is for */}
                <ClientContextIndicator label={editingDocument ? "Editing for" : "Creating for"} />
                {!editingDocument && (
                  <DraftRestoreBanner
                    visible={draft.hasDraft}
                    savedAt={draft.draftSavedAt}
                    label={createType === 'quotation' ? 'quotation' : 'invoice'}
                    onRestore={() => {
                      const restored = draft.restore();
                      if (restored) setFormData(restored);
                    }}
                    onDiscard={draft.discard}
                  />
                )}
                <form onSubmit={handleCreate} className="space-y-4 min-w-0">
                  {/* Document Type Toggle - Only show when creating new */}
                  {!editingDocument && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={createType === 'quotation' ? 'default' : 'outline'}
                        onClick={() => setCreateType('quotation')}
                        className="flex-1"
                      >
                        <FileText size={18} className="mr-2" />
                        Quotation
                      </Button>
                      <Button
                        type="button"
                        variant={createType === 'invoice' ? 'default' : 'outline'}
                        onClick={() => setCreateType('invoice')}
                        className="flex-1"
                      >
                        <Receipt size={18} className="mr-2" />
                        Invoice
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Customer *</Label>
                      {!showNewCustomer ? (
                        <div className="flex gap-2">
                          <Select value={formData.customer_id} onValueChange={(v) => setFormData(p => ({ ...p, customer_id: v }))}>
                            <SelectTrigger data-testid="doc-customer" className="flex-1">
                              <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {contacts.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.company_name || c.name}
                                  {c.company_name && c.name && c.name.toLowerCase() !== c.company_name.toLowerCase() ? ` — ${c.name}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="icon"
                            onClick={() => setShowNewCustomer(true)}
                            title="Create new customer"
                            data-testid="add-new-customer-btn"
                          >
                            <UserPlus size={18} />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">New Customer</span>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setShowNewCustomer(false)}
                            >
                              Cancel
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input
                              placeholder="Name *"
                              value={newCustomer.name}
                              onChange={(e) => setNewCustomer(p => ({ ...p, name: e.target.value }))}
                              data-testid="new-customer-name"
                            />
                            <Input
                              placeholder="Email *"
                              type="email"
                              value={newCustomer.email}
                              onChange={(e) => setNewCustomer(p => ({ ...p, email: e.target.value }))}
                              data-testid="new-customer-email"
                            />
                            <Input
                              placeholder="Phone"
                              value={newCustomer.phone}
                              onChange={(e) => setNewCustomer(p => ({ ...p, phone: e.target.value }))}
                              data-testid="new-customer-phone"
                            />
                            <Input
                              placeholder="Company Name"
                              value={newCustomer.company_name}
                              onChange={(e) => setNewCustomer(p => ({ ...p, company_name: e.target.value }))}
                              data-testid="new-customer-company"
                            />
                          </div>
                          <Input
                            placeholder="Address"
                            value={newCustomer.address}
                            onChange={(e) => setNewCustomer(p => ({ ...p, address: e.target.value }))}
                            data-testid="new-customer-address"
                          />
                          <Button 
                            type="button" 
                            size="sm" 
                            onClick={handleCreateCustomer}
                            data-testid="save-new-customer-btn"
                          >
                            <Plus size={14} className="mr-1" /> Create & Select
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))}
                        data-testid="doc-date"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {createType === 'invoice' && (
                      <div className="space-y-2">
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          value={formData.due_date}
                          onChange={(e) => setFormData(p => ({ ...p, due_date: e.target.value }))}
                          data-testid="doc-due-date"
                        />
                      </div>
                    )}

                    {createType === 'invoice' && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <FileText size={14} /> Invoice Type
                        </Label>
                        <Select
                          value={formData.invoice_type || 'tax_invoice'}
                          onValueChange={(v) => setFormData(p => ({ ...p, invoice_type: v }))}
                        >
                          <SelectTrigger data-testid="doc-invoice-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tax_invoice">Tax Invoice (VAT registered)</SelectItem>
                            <SelectItem value="proforma_invoice">Proforma Invoice (not a tax document)</SelectItem>
                            <SelectItem value="invoice">Invoice (no "TAX" wording)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground">
                          {formData.invoice_type === 'proforma_invoice'
                            ? 'Proforma invoices are quotes for payment — they will NOT include a QR verification code on the PDF.'
                            : formData.invoice_type === 'invoice'
                            ? 'Generic invoice — VAT-registered TRN is still printed if set, but the title says only "Invoice".'
                            : 'Tax invoice — full UAE VAT compliant format with QR.'}
                        </p>
                      </div>
                    )}

                    {createType === 'invoice' && !editingDocument && (
                      <div className="space-y-2">
                        <Label>Link to Quotation</Label>
                        <select
                          value={formData.quotation_id}
                          onChange={(e) => {
                            const qid = e.target.value;
                            if (!qid) {
                              setFormData(p => ({ ...p, quotation_id: '' }));
                              return;
                            }
                            const q = quotations.find(qt => qt.id === qid);
                            if (!q) return;
                            // Approval guard: if the quote isn't already in an
                            // approved status, intercept and show a confirmation
                            // dialog BEFORE prefilling the invoice form.
                            if (!QUOTE_APPROVED_STATUSES.includes(q.status)) {
                              setApprovalGuard({ open: true, quote: q });
                              return;
                            }
                            applyQuotationToInvoice(q);
                          }}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="doc-link-quotation"
                        >
                          <option value="">No linked quotation</option>
                          {(() => {
                            // Show every open quotation — only hide trashed
                            // ones.  Sort newest first so the most recent
                            // candidate sits at the top.
                            const HIDDEN = new Set(['cancelled', 'deleted', 'rejected', 'archived']);
                            const list = quotations
                              .filter(q => !q.is_deleted && !HIDDEN.has(q.status))
                              .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                            if (list.length === 0) {
                              return <option value="" disabled>No quotations available</option>;
                            }
                            return list.map(q => (
                              <option key={q.id} value={q.id}>
                                {q.document_number} — {q.customer_name} ({formatCurrency(q.total)}) · {q.status?.replace(/_/g, ' ')}
                              </option>
                            ));
                          })()}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Picking a quotation auto-fills the customer, line items, project ref &amp; notes — you can still edit anything before saving.
                        </p>
                      </div>
                    )}
                    
                    {createType === 'quotation' && (
                      <div className="space-y-2">
                        <Label>Valid Until <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={formData.valid_until}
                          onChange={(e) => setFormData(p => ({ ...p, valid_until: e.target.value }))}
                          required
                          data-testid="doc-valid-until"
                        />
                      </div>
                    )}

                    {createType === 'quotation' && (
                      <div className="space-y-2">
                        <Label>Payment Terms <span className="text-red-500">*</span></Label>
                        {!customPaymentTermsMode ? (
                          <select
                            value={formData.payment_terms}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__custom__') {
                                setCustomPaymentTermsMode(true);
                                setFormData(p => ({ ...p, payment_terms: '' }));
                              } else {
                                setFormData(p => ({ ...p, payment_terms: v }));
                              }
                            }}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            required
                            data-testid="doc-payment-terms"
                          >
                            <option value="">Select payment terms...</option>
                            <option value="advance">100% Advance</option>
                            <option value="50_advance">50% Advance, 50% on Delivery</option>
                            <option value="cod">Cash on Delivery</option>
                            <option value="net15">Net 15 Days</option>
                            <option value="net30">Net 30 Days</option>
                            <option value="net45">Net 45 Days</option>
                            <option value="net60">Net 60 Days</option>
                            <option value="credit">Credit (as per agreement)</option>
                            <option value="__custom__">✏️ Type custom…</option>
                          </select>
                        ) : (
                          <div className="space-y-1">
                            <Input
                              value={formData.payment_terms}
                              onChange={(e) => setFormData(p => ({ ...p, payment_terms: e.target.value }))}
                              placeholder="e.g., 30% advance, 60% on delivery, 10% retention 30 days"
                              required
                              data-testid="doc-payment-terms-custom"
                              autoFocus
                            />
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline"
                              onClick={() => {
                                setCustomPaymentTermsMode(false);
                                setFormData(p => ({ ...p, payment_terms: '' }));
                              }}
                              data-testid="doc-payment-terms-back-to-presets"
                            >
                              ← Choose from presets instead
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Reference Field - For both Quotation and Invoice */}
                    <div className="space-y-2">
                      <Label>Reference</Label>
                      <Input
                        value={createType === 'quotation' ? formData.reference : formData.po_reference}
                        onChange={(e) => setFormData(p => ({ 
                          ...p, 
                          [createType === 'quotation' ? 'reference' : 'po_reference']: e.target.value 
                        }))}
                        placeholder="Customer reference number..."
                        data-testid="doc-reference"
                      />
                    </div>

                    {/* Saved Projects — populated from the selected customer's
                        contact card.  Picking one auto-fills Project Ref and
                        Location below.  Manual entry still works after that. */}
                    {(() => {
                      const cust = contacts.find(c => c.id === formData.customer_id);
                      const projects = (cust && Array.isArray(cust.projects)) ? cust.projects : [];
                      if (!formData.customer_id) return null;
                      return (
                        <div className="space-y-2">
                          <Label>Project (from customer card)</Label>
                          <div className="flex gap-2">
                            <select
                              value={formData.project_reference && projects.find(p => p.reference === formData.project_reference || p.name === formData.project_reference) ? (projects.find(p => p.reference === formData.project_reference || p.name === formData.project_reference).id || formData.project_reference) : ''}
                              onChange={(e) => {
                                const pid = e.target.value;
                                if (!pid) return;
                                const proj = projects.find(p => (p.id || p.reference || p.name) === pid);
                                if (proj) {
                                  setFormData(prev => ({
                                    ...prev,
                                    project_reference: proj.reference || proj.name || '',
                                    project_location: proj.location || prev.project_location,
                                  }));
                                }
                              }}
                              className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              data-testid="doc-project-picker"
                              disabled={projects.length === 0}
                            >
                              <option value="">{projects.length === 0 ? 'No projects saved on this customer — add below or in their contact card' : 'Pick a project…'}</option>
                              {projects
                                .filter(p => (p.status || 'active') === 'active')
                                .map((p) => (
                                  <option key={p.id || p.reference || p.name} value={p.id || p.reference || p.name}>
                                    {p.name}{p.reference ? ` (${p.reference})` : ''}{p.location ? ` — ${p.location}` : ''}
                                  </option>
                                ))}
                            </select>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const name = window.prompt('New project name:');
                                if (!name) return;
                                const location = window.prompt('Project location (optional):') || '';
                                const reference = window.prompt('Reference number (optional):') || '';
                                const next = [...(cust.projects || []), { name, location, reference, status: 'active' }];
                                try {
                                  await axios.patch(`${API_URL}/contacts/${cust.id}`, { projects: next });
                                  toast.success(`Project "${name}" added to ${cust.company_name || cust.name}`);
                                  const res = await axios.get(`${API_URL}/contacts?contact_type=customer`);
                                  setContacts(res.data || []);
                                  setFormData(prev => ({
                                    ...prev,
                                    project_reference: reference || name,
                                    project_location: location || prev.project_location,
                                  }));
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to add project');
                                }
                              }}
                              data-testid="doc-project-quick-add"
                              title="Save a new project to this customer for future reuse"
                            >
                              + New
                            </Button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Project Reference & Location (searchable in summary) */}
                    <div className="space-y-2">
                      <Label>Project Reference</Label>
                      <Input
                        value={formData.project_reference || ''}
                        onChange={(e) => setFormData(p => ({ ...p, project_reference: e.target.value }))}
                        placeholder="e.g., PRJ-2026-001 or 'Marina Tower Phase 2'"
                        data-testid="doc-project-reference"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Project Location</Label>
                      <Input
                        value={formData.project_location || ''}
                        onChange={(e) => setFormData(p => ({ ...p, project_location: e.target.value }))}
                        placeholder="e.g., Dubai Marina, Site B"
                        data-testid="doc-project-location"
                      />
                    </div>
                    
                    {/* Overall Discount - Only for Quotations */}
                    {createType === 'quotation' && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Percent size={14} /> Overall Discount
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.overall_discount || ''}
                            onChange={(e) => setFormData(p => ({ ...p, overall_discount: parseFloat(e.target.value) || 0 }))}
                            placeholder="0.00"
                            className="flex-1 font-mono"
                            data-testid="doc-overall-discount"
                          />
                          <Select 
                            value={formData.overall_discount_type} 
                            onValueChange={(v) => setFormData(p => ({ ...p, overall_discount_type: v }))}
                          >
                            <SelectTrigger className="w-24" data-testid="doc-discount-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">%</SelectItem>
                              <SelectItem value="fixed">Fixed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {/* Invoice-only — Billing Type (regular / progress) */}
                    {createType === 'invoice' && (
                      <div className="space-y-2 col-span-1 sm:col-span-2 rounded-md border bg-muted/30 p-3">
                        <Label className="flex items-center gap-2">
                          <Receipt size={14} /> Billing Type
                        </Label>
                        <Select
                          value={formData.billing_type || 'final'}
                          onValueChange={(v) => setFormData(p => ({
                            ...p,
                            billing_type: v,
                            progress_mode: v === 'progress' ? (p.progress_mode || 'overall') : null,
                          }))}
                        >
                          <SelectTrigger data-testid="doc-billing-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="final">Full / Final Invoice</SelectItem>
                            <SelectItem value="advance">Advance Invoice</SelectItem>
                            <SelectItem value="progress">Progress / Part-billing Invoice</SelectItem>
                          </SelectContent>
                        </Select>
                        {formData.billing_type === 'progress' && (
                          <div className="space-y-3 mt-2 rounded-md border bg-card p-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Progress Mode</Label>
                              <div className="inline-flex rounded-md border bg-muted text-xs overflow-hidden">
                                {['overall', 'per_line'].map((m) => (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, progress_mode: m }))}
                                    className={`px-3 py-1 transition-colors ${formData.progress_mode === m ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/70'}`}
                                    data-testid={`doc-progress-mode-${m}`}
                                  >
                                    {m === 'overall' ? 'Overall %' : 'Per-line %'}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {formData.progress_mode === 'overall' && (
                              <div className="space-y-1">
                                <Label className="text-xs">Overall Progress %</Label>
                                <Input
                                  type="number" min="0" max="100" step="0.01"
                                  value={formData.progress_percent ?? 100}
                                  onChange={(e) => setFormData(p => ({ ...p, progress_percent: parseFloat(e.target.value) || 0 }))}
                                  placeholder="e.g. 30 for 30% of contract value"
                                  data-testid="doc-progress-percent"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Scales the total of the invoice (e.g. 30% of AED 100,000 = AED 30,000).
                                </p>
                              </div>
                            )}
                            {formData.progress_mode === 'per_line' && (
                              <p className="text-xs text-muted-foreground">
                                Set the "% complete" on each line in the items table below (column appears automatically).
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Invoice-only — Deductions from total */}
                    {createType === 'invoice' && (
                      <div className="space-y-2 col-span-1 sm:col-span-2 rounded-md border bg-muted/30 p-3">
                        <Label className="flex items-center gap-2">
                          <Money size={14} /> Deductions (subtract from invoice total)
                        </Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Retention</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              value={formData.retention_amount || ''}
                              onChange={(e) => setFormData(p => ({ ...p, retention_amount: parseFloat(e.target.value) || 0 }))}
                              placeholder="0.00"
                              className="text-right font-mono"
                              data-testid="doc-retention"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Advance Received</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              value={formData.advance_payment || ''}
                              onChange={(e) => setFormData(p => ({ ...p, advance_payment: parseFloat(e.target.value) || 0 }))}
                              placeholder="0.00"
                              className="text-right font-mono"
                              data-testid="doc-advance"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Previous Payments</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              value={formData.previous_payments || ''}
                              onChange={(e) => setFormData(p => ({ ...p, previous_payments: parseFloat(e.target.value) || 0 }))}
                              placeholder="0.00"
                              className="text-right font-mono"
                              data-testid="doc-prev-payments"
                            />
                          </div>
                        </div>
                        {(() => {
                          const retention = parseFloat(formData.retention_amount) || 0;
                          const advance = parseFloat(formData.advance_payment) || 0;
                          const previous = parseFloat(formData.previous_payments) || 0;
                          if ((retention + advance + previous) <= 0) return null;
                          const taxable = parseFloat(totals.taxableAmount) || 0;
                          const tax = parseFloat(totals.taxTotal) || 0;
                          const grossWithTax = parseFloat(totals.total) || 0;
                          const finalPay = parseFloat(totals.finalPayable) || 0;
                          return (
                            <div className="rounded-md bg-muted/30 border border-border px-3 py-2 mt-3 text-sm space-y-1 font-mono">
                              {retention > 0 && (
                                <>
                                  <div className="flex justify-between"><span>Net Taxable (Sub-Total − Retention)</span><span>{formatCurrency(taxable)}</span></div>
                                  <div className="flex justify-between text-xs text-muted-foreground"><span>Tax computed on the net taxable amount</span><span>{formatCurrency(tax)}</span></div>
                                </>
                              )}
                              <div className="flex justify-between"><span>Total Amount Including VAT</span><span className="font-semibold">{formatCurrency(grossWithTax)}</span></div>
                              {advance > 0 && <div className="flex justify-between text-muted-foreground"><span>Less: Advance Received</span><span>-{formatCurrency(advance)}</span></div>}
                              {previous > 0 && <div className="flex justify-between text-muted-foreground"><span>Less: Previously Paid</span><span>-{formatCurrency(previous)}</span></div>}
                              <div className="flex justify-between pt-1 border-t border-border">
                                <span className="font-semibold">Current Payable</span>
                                <span className="font-bold text-rose-700 dark:text-rose-400" data-testid="doc-final-payable">
                                  {formatCurrency(finalPay)}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* VAT Category - UAE Compliance */}
                    <div className="space-y-2">
                      <Label>VAT Category (UAE)</Label>
                      <Select 
                        value={formData.vat_category} 
                        onValueChange={(v) => {
                          // Update line tax rates based on VAT category. Only
                          // "standard" keeps the 5% per-line; every other
                          // category enforces 0% across all lines so the user
                          // cannot accidentally save a misaligned tax_rate.
                          const taxRate = v === 'standard' ? 5 : 0;
                          setFormData(p => ({ 
                            ...p, 
                            vat_category: v,
                            lines: p.lines.map(l => ({ ...l, tax_rate: taxRate }))
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="doc-vat-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard Rated (5%)</SelectItem>
                          <SelectItem value="zero_rated">Zero Rated (0%)</SelectItem>
                          <SelectItem value="exempt">Exempt</SelectItem>
                          <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                          <SelectItem value="not_yet_due">Not Yet Due (Deferred VAT)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {formData.vat_category === 'standard' && 'Subject to 5% UAE VAT'}
                        {formData.vat_category === 'zero_rated' && 'Exports, international transport - VAT recoverable'}
                        {formData.vat_category === 'exempt' && 'Financial services, residential property - No VAT'}
                        {formData.vat_category === 'out_of_scope' && 'Outside UAE VAT system'}
                        {formData.vat_category === 'not_yet_due' && 'VAT deferred — chargeable later (import/reverse-charge). Total excludes VAT now.'}
                      </p>
                      {formData.vat_category && formData.vat_category !== 'standard' && (
                        <div className="text-[11px] rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 px-2 py-1.5 flex items-center gap-1.5" data-testid="vat-zero-confirm">
                          <CheckCircle size={12} weight="fill" />
                          <span>No VAT applied — total = subtotal − discounts. Per-line tax rates are locked at 0%.</span>
                        </div>
                      )}

                      {/* VAT-Inclusive Pricing Toggle — only meaningful when Standard-rated */}
                      {(formData.vat_category || 'standard') === 'standard' && (
                        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium flex items-center gap-2">
                              Prices are VAT-inclusive
                              {formData.is_vat_inclusive && (
                                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Incl.</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {formData.is_vat_inclusive
                                ? 'Each unit price already contains 5% VAT. Subtotal shown net; VAT is back-calculated.'
                                : 'Unit prices are net of VAT. 5% VAT will be added on top.'}
                            </p>
                          </div>
                          <Switch
                            checked={!!formData.is_vat_inclusive}
                            onCheckedChange={(v) => setFormData(p => ({ ...p, is_vat_inclusive: !!v }))}
                            data-testid="doc-vat-inclusive-toggle"
                          />
                        </div>
                      )}
                    </div>

                    {/* PDF Layout override (per-document) */}
                    <div className="space-y-2">
                      <Label>PDF Layout</Label>
                      <div className="flex items-center gap-2">
                        <select
                          value={formData.layout_template || ''}
                          onChange={(e) => setFormData(p => ({ ...p, layout_template: e.target.value }))}
                          className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="doc-layout-template"
                        >
                          <option value="">Use company default</option>
                          <option value="classic">Classic — split header</option>
                          <option value="modern_band">Modern Band — coloured header</option>
                          <option value="minimalist">Minimalist — black &amp; white</option>
                          <option value="compact">Compact — tighter spacing</option>
                        </select>
                        <LayoutPreviewButton layout={formData.layout_template || 'classic'} />
                      </div>
                      <p className="text-xs text-muted-foreground">Overrides the company default for this document only.</p>
                    </div>

                    {/* Hide Audit Trail toggle */}
                    <div className="space-y-2 md:col-span-2">
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-input bg-background hover:bg-accent/30 transition-colors">
                        <input
                          type="checkbox"
                          checked={!!formData.hide_audit_trail}
                          onChange={(e) => setFormData(p => ({ ...p, hide_audit_trail: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 rounded border-input"
                          data-testid="doc-hide-audit-trail"
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-medium">Hide Audit Trail on PDF</span>
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            Removes the &quot;Prepared By / Approved By / Last Edited&quot; block with system timestamps from the generated PDF. Useful for back-dated or pre-dated client copies where only the document&apos;s date should be visible.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Line Items</Label>
                      <div className="flex items-center gap-2">
                        {(createType === 'quotation' || editingDocument?.docType === 'quotation') && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setBoqUploadOpen(true)}
                            data-testid="upload-boq-btn"
                            title="Upload Excel / CSV / Word / PDF / image and auto-fill line items"
                          >
                            <Sparkle size={14} className="mr-1" /> Upload BOQ
                          </Button>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                          <Plus size={14} className="mr-1" /> Add Line
                        </Button>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg overflow-x-auto min-w-0">
                      <table className="w-full text-sm" style={{ minWidth: '880px' }}>
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Description</th>
                            <th className="text-left px-3 py-2 font-medium w-44">Specification</th>
                            <th className="text-right px-3 py-2 font-medium w-20">Qty</th>
                            <th className="text-left px-3 py-2 font-medium w-24">Unit</th>
                            <th className="text-right px-3 py-2 font-medium w-40">Price</th>
                            <th className="text-right px-3 py-2 font-medium w-20">Tax %</th>
                            {showPerLineProgress && <th className="text-right px-3 py-2 font-medium w-24">% Billed</th>}
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {formData.lines.map((line, index) => {
                            const UNIT_PRESETS = ['Nos', 'Mtr', 'Litre', 'Lot', 'Kg'];
                            const unitVal = line.unit || '';
                            const isCustomUnit = unitVal !== '' && !UNIT_PRESETS.includes(unitVal);
                            return (
                            <tr key={index}>
                              <td className="px-2 py-2">
                                <Textarea
                                  value={line.description}
                                  onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                  placeholder="Item description"
                                  className="min-h-[36px] resize-y text-sm"
                                  rows={2}
                                  data-testid={`line-${index}-desc`}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Textarea
                                  value={line.specification || ''}
                                  onChange={(e) => handleLineChange(index, 'specification', e.target.value)}
                                  placeholder="Brand / model / specs"
                                  className="min-h-[36px] resize-y text-sm"
                                  rows={2}
                                  data-testid={`line-${index}-spec`}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={line.quantity ?? ''}
                                  onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                                  placeholder="0"
                                  className="h-8 text-right font-mono"
                                  data-testid={`line-${index}-qty`}
                                />
                              </td>
                              <td className="px-2 py-2">
                                {!isCustomUnit ? (
                                  <select
                                    value={unitVal}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '__custom__') {
                                        // Sentinel: switch to custom-fill on next render
                                        handleLineChange(index, 'unit', ' ');
                                      } else {
                                        handleLineChange(index, 'unit', v);
                                      }
                                    }}
                                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    data-testid={`line-${index}-unit`}
                                  >
                                    <option value="">—</option>
                                    {UNIT_PRESETS.map((u) => (
                                      <option key={u} value={u}>{u}</option>
                                    ))}
                                    <option value="__custom__">✏️ Custom…</option>
                                  </select>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={unitVal.trim()}
                                      onChange={(e) => handleLineChange(index, 'unit', e.target.value)}
                                      placeholder="e.g. sq.ft"
                                      className="h-8 text-xs"
                                      data-testid={`line-${index}-unit-custom`}
                                      autoFocus={unitVal.trim() === ''}
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-6 shrink-0 text-muted-foreground"
                                      onClick={() => handleLineChange(index, 'unit', '')}
                                      title="Back to preset list"
                                      data-testid={`line-${index}-unit-reset`}
                                    >
                                      <X size={12} />
                                    </Button>
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={line.unit_price ?? ''}
                                  onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)}
                                  placeholder="0.00"
                                  className="h-8 text-right font-mono"
                                  data-testid={`line-${index}-price`}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={line.tax_rate ?? ''}
                                  onChange={(e) => handleLineChange(index, 'tax_rate', e.target.value)}
                                  placeholder="0"
                                  disabled={formData.vat_category && formData.vat_category !== 'standard'}
                                  title={
                                    formData.vat_category && formData.vat_category !== 'standard'
                                      ? `VAT locked at 0% because the document VAT category is "${formData.vat_category.replaceAll('_', ' ')}". Change VAT Category to "Standard Rated" to edit per-line tax rates.`
                                      : 'Enter the line VAT percentage (0-100).'
                                  }
                                  className="h-8 text-right font-mono disabled:bg-muted disabled:text-muted-foreground"
                                  data-testid={`line-${index}-tax`}
                                />
                              </td>
                              {showPerLineProgress && (
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={line.line_progress_percent ?? 100}
                                    onChange={(e) => handleLineChange(index, 'line_progress_percent', e.target.value)}
                                    className="h-8 text-right font-mono"
                                    data-testid={`line-${index}-progress-pct`}
                                  />
                                </td>
                              )}
                              <td className="px-2 py-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveLine(index)}
                                  className="h-8 w-8"
                                  disabled={formData.lines.length <= 1}
                                >
                                  <Trash size={14} />
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-muted/30">
                          {totals.isProgress && (
                            <tr className="text-muted-foreground" data-testid="doc-contract-value-row">
                              <td colSpan={showPerLineProgress ? 6 : 5} className="px-3 py-2 text-right font-medium">
                                Contract value (100%, excl. VAT):
                              </td>
                              <td colSpan={2} className="px-3 py-2 text-right font-mono">{formatCurrency(parseFloat(totals.contractSubtotal))}</td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan={showPerLineProgress ? 6 : 5} className="px-3 py-2 text-right font-medium">
                              {totals.isProgress ? `This invoice (${totals.progressLabel}) subtotal` : 'Subtotal'}{formData.is_vat_inclusive ? ' (excl. VAT)' : ''}:
                            </td>
                            <td colSpan={2} className="px-3 py-2 text-right font-mono" data-testid="doc-subtotal">{formatCurrency(parseFloat(totals.subtotal))}</td>
                          </tr>
                          {createType === 'quotation' && parseFloat(totals.overallDiscount) > 0 && (
                            <tr className="text-muted-foreground">
                              <td colSpan={showPerLineProgress ? 6 : 5} className="px-3 py-2 text-right font-medium">
                                Overall Discount ({formData.overall_discount_type === 'percentage' ? `${formData.overall_discount}%` : 'Fixed'}):
                              </td>
                              <td colSpan={2} className="px-3 py-2 text-right font-mono">-{formatCurrency(parseFloat(totals.overallDiscount))}</td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan={showPerLineProgress ? 6 : 5} className="px-3 py-2 text-right font-medium">Tax:</td>
                            <td colSpan={2} className="px-3 py-2 text-right font-mono" data-testid="doc-tax">{formatCurrency(parseFloat(totals.taxTotal))}</td>
                          </tr>
                          <tr className="font-bold text-lg">
                            <td colSpan={showPerLineProgress ? 6 : 5} className="px-3 py-2 text-right">{totals.isProgress ? 'This Invoice Total:' : 'Total:'}</td>
                            <td colSpan={2} className="px-3 py-2 text-right font-mono" data-testid="doc-total">{formatCurrency(parseFloat(totals.total))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Quotation-specific rich sections */}
                  {(createType === 'quotation' || editingDocument?.docType === 'quotation') && (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="doc-salutation">Salutation / Intro</Label>
                          <span className="text-xs text-muted-foreground">Printed above the line-items table on the quotation PDF</span>
                        </div>
                        <Textarea
                          id="doc-salutation"
                          value={formData.salutation || ''}
                          onChange={(e) => setFormData(p => ({ ...p, salutation: e.target.value }))}
                          rows={3}
                          placeholder={"e.g. Dear Sir,\n\nWe are pleased to submit our detailed quotation as per your enquiry. Kindly find below our offer for your kind review and approval."}
                          data-testid="doc-salutation"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="doc-scope">Scope of Works</Label>
                          <span className="text-xs text-muted-foreground">Detailed scope — appears on quotation PDF</span>
                        </div>
                        <Textarea
                          id="doc-scope"
                          value={formData.scope_of_works || ''}
                          onChange={(e) => setFormData(p => ({ ...p, scope_of_works: e.target.value }))}
                          rows={6}
                          placeholder={"Describe the full scope here. Use line breaks for paragraphs. Example:\n\nSupply, install, test and commission the fire alarm system as per UAE Civil Defence requirements.\n\nThe system shall include addressable smoke detectors, manual call points, sounders/strobes, and a 24V control panel with battery backup."}
                          data-testid="doc-scope-of-works"
                        />
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="doc-exclusions">Exclusions</Label>
                            <span className="text-xs text-muted-foreground">Auto-numbered on PDF</span>
                          </div>
                          <Textarea
                            id="doc-exclusions"
                            value={formData.exclusions || ''}
                            onChange={(e) => setFormData(p => ({ ...p, exclusions: e.target.value }))}
                            rows={5}
                            placeholder={"One exclusion per line. Example:\n1. Civil works and chasing of walls\n2. Power supply to FACP\n3. Trunking & containment beyond layout"}
                            data-testid="doc-exclusions"
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label htmlFor="doc-payment-detail">Payment Terms (detailed)</Label>
                            <span className="text-xs text-muted-foreground">Auto-numbered on PDF</span>
                          </div>
                          <Textarea
                            id="doc-payment-detail"
                            value={formData.payment_terms_detail || ''}
                            onChange={(e) => setFormData(p => ({ ...p, payment_terms_detail: e.target.value }))}
                            rows={5}
                            placeholder={"One milestone per line. Example:\n1. 30% advance against PO\n2. 40% on delivery\n3. 20% on completion\n4. 10% retention 30 days post-handover"}
                            data-testid="doc-payment-terms-detail"
                          />
                        </div>
                      </div>

                      {/* Attachments — drawings, images, supporting PDFs */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Attachments</Label>
                          <span className="text-xs text-muted-foreground">Drawings, images & supporting PDFs (max 20MB each)</span>
                        </div>
                        <QuotationAttachmentField
                          documentId={editingDocument?.docType === 'quotation' ? editingDocument?.id : null}
                          documentType="quotation"
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                      rows={2}
                      placeholder="Additional notes..."
                      data-testid="doc-notes"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Terms & Conditions</Label>
                    <Textarea
                      value={formData.terms_conditions}
                      onChange={(e) => setFormData(p => ({ ...p, terms_conditions: e.target.value }))}
                      rows={3}
                      placeholder="Enter terms and conditions (leave empty to use company defaults)..."
                      data-testid="doc-terms"
                    />
                  </div>

                  <DialogFooter className="flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
                    {!editingDocument && (
                      <div className="mr-auto">
                        <DraftSavedIndicator savedAt={draft.lastAutoSavedAt} />
                      </div>
                    )}
                    {/* Save as Revision toggle — only when editing a quotation */}
                    {editingDocument && editingDocument.docType === 'quotation' && (() => {
                      const currRev = editingDocument.revision || 'R0';
                      const nextRev = `R${parseInt(currRev.replace('R', ''), 10) + 1}`;
                      const isLocked = !isQuoteEditable(editingDocument.status);
                      if (isLocked) {
                        // Locked status — revision is forced
                        return (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-xs flex-1 mr-auto" data-testid="edit-revision-forced-note">
                            <Warning size={14} className="shrink-0" weight="fill" />
                            <span>This quotation is <strong>{statusLabels[editingDocument.status]}</strong>; saving will create new revision <strong className="font-mono">{nextRev}</strong>.</span>
                          </div>
                        );
                      }
                      // Editable status — let the user choose
                      return (
                        <label
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs flex-1 mr-auto transition-colors ${
                            saveAsRevision
                              ? 'bg-violet-500/10 border-violet-500/40 text-violet-700 dark:text-violet-300'
                              : 'bg-muted/30 border-border hover:bg-muted/50'
                          }`}
                          data-testid="edit-revision-toggle-label"
                        >
                          <input
                            type="checkbox"
                            checked={saveAsRevision}
                            onChange={(e) => setSaveAsRevision(e.target.checked)}
                            className="accent-violet-500"
                            data-testid="edit-revision-toggle"
                          />
                          <span>
                            Save as new revision
                            <span className="font-mono font-bold ml-1.5">{nextRev}</span>
                            <span className="text-muted-foreground ml-1.5">(current {currRev})</span>
                          </span>
                        </label>
                      );
                    })()}
                    <Button type="submit" disabled={formLoading} data-testid="doc-submit">
                      {formLoading 
                        ? <CircleNotch className="animate-spin" size={18} /> 
                        : editingDocument 
                          ? (saveAsRevision || (editingDocument.docType === 'quotation' && !isQuoteEditable(editingDocument.status))
                              ? `Save as Revision`
                              : `Update ${editingDocument.docType === 'quotation' ? 'Quotation' : 'Invoice'}`)
                          : `Create ${createType === 'quotation' ? 'Quotation' : 'Invoice'}`
                      }
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* AI BOQ Upload Dialog (lives outside the create-document <Dialog> so
              the two modals don't fight for focus).  */}
          <BoqUploadDialog
            open={boqUploadOpen}
            onOpenChange={setBoqUploadOpen}
            existingLines={formData.lines}
            onCommit={(nextLines) => {
              setFormData((prev) => ({ ...prev, lines: nextLines }));
            }}
          />

          {/* Revision diff viewer — opens from the kebab menu on any quotation row */}
          <RevisionDiffViewer
            open={diffViewer.open}
            onClose={() => setDiffViewer({ open: false, quotationId: null, documentNumber: '' })}
            quotationId={diffViewer.quotationId}
            documentNumber={diffViewer.documentNumber}
          />

          {/* Link Existing Invoice dialog — retro-attach an already-created
              invoice to an old quotation (paired with the /link-invoice
              backend). Only shows invoices from the same customer that aren't
              already linked to a different quotation. */}
          <Dialog
            open={linkInvoiceDialog.open}
            onOpenChange={(o) => { if (!o) setLinkInvoiceDialog({ open: false, quotation: null, selectedInvoiceId: '' }); }}
          >
            <DialogContent className="max-w-lg" data-testid="link-invoice-dialog">
              <DialogHeader>
                <DialogTitle>Link Existing Invoice to Quotation</DialogTitle>
              </DialogHeader>
              {linkInvoiceDialog.quotation && (
                <div className="space-y-3">
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Quotation</span><span className="font-mono">{linkInvoiceDialog.quotation.document_number}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{linkInvoiceDialog.quotation.customer_name}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono">{Number(linkInvoiceDialog.quotation.total || 0).toFixed(2)}</span></div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Pick an invoice raised for this customer
                    </label>
                    <select
                      value={linkInvoiceDialog.selectedInvoiceId}
                      onChange={(e) => setLinkInvoiceDialog(l => ({ ...l, selectedInvoiceId: e.target.value }))}
                      className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="link-invoice-select"
                    >
                      <option value="">-- Select an invoice --</option>
                      {(invoices || [])
                        .filter(inv =>
                          inv.customer_id === linkInvoiceDialog.quotation?.customer_id &&
                          // Not already linked to a different quotation
                          (!inv.quotation_id || inv.quotation_id === linkInvoiceDialog.quotation?.id)
                        )
                        .map(inv => (
                          <option key={inv.id} value={inv.id}>
                            {inv.document_number} · {inv.date?.slice(0, 10)} · {Number(inv.total || 0).toFixed(2)}
                            {inv.status ? ` · ${inv.status}` : ''}
                          </option>
                        ))}
                    </select>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Only invoices for the same customer that aren&apos;t already linked to another quotation are shown.
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setLinkInvoiceDialog({ open: false, quotation: null, selectedInvoiceId: '' })}>Cancel</Button>
                <Button
                  onClick={confirmLinkInvoice}
                  disabled={!linkInvoiceDialog.selectedInvoiceId}
                  data-testid="link-invoice-confirm-btn"
                >
                  Link Invoice
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Convert-to-Invoice preview dialog. Shows any advance receipt
              vouchers already booked against the quotation, the resulting
              balance to invoice, and asks the user to pick a due date before
              confirming. Backed by /quotations/{id}/conversion-preview. */}
          <Dialog
            open={convertDialog.open}
            onOpenChange={(o) => { if (!o) setConvertDialog({ open: false, loading: false, preview: null, dueDate: '' }); }}
          >
            <DialogContent className="max-w-lg" data-testid="convert-to-invoice-dialog">
              <DialogHeader>
                <DialogTitle>Convert Quotation to Invoice</DialogTitle>
              </DialogHeader>
              {convertDialog.loading || !convertDialog.preview ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Loading preview…</div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Quotation</span><span className="font-mono">{convertDialog.preview.document_number}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{convertDialog.preview.customer_name}</span></div>
                  </div>

                  <div className="rounded-md border p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Quotation Total</span><span className="font-mono">{convertDialog.preview.total.toFixed(2)}</span></div>
                    <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                      <span>− Advance Received</span>
                      <span className="font-mono">{convertDialog.preview.advance_paid.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1">
                      <span>Balance to Invoice</span>
                      <span className="font-mono">{convertDialog.preview.balance_to_invoice.toFixed(2)}</span>
                    </div>
                  </div>

                  {convertDialog.preview.vouchers?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">
                        Advance Vouchers ({convertDialog.preview.vouchers.length}) — will be re-linked, not duplicated
                      </div>
                      <div className="rounded-md border overflow-hidden max-h-32 overflow-y-auto">
                        <table className="w-full text-xs">
                          <tbody className="divide-y">
                            {convertDialog.preview.vouchers.map(v => (
                              <tr key={v.id}>
                                <td className="px-2 py-1 font-mono">{v.voucher_number}</td>
                                <td className="px-2 py-1 text-muted-foreground">{v.date}</td>
                                <td className="px-2 py-1 text-right font-mono">{Number(v.total_amount || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {convertDialog.preview.vouchers?.length === 0 && (
                    <div className="text-xs text-muted-foreground italic">
                      No receipt voucher linked to this quotation. To carry over a customer advance, first raise a receipt voucher with this quotation selected in the &quot;Linked Quotation&quot; field.
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Invoice Due Date</label>
                    <input
                      type="date"
                      value={convertDialog.dueDate}
                      onChange={(e) => setConvertDialog(c => ({ ...c, dueDate: e.target.value }))}
                      className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                      data-testid="convert-due-date"
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setConvertDialog({ open: false, loading: false, preview: null, dueDate: '' })}>Cancel</Button>
                <Button onClick={confirmConvertToInvoice} disabled={convertDialog.loading || !convertDialog.preview} data-testid="convert-confirm-btn">
                  Confirm &amp; Create Invoice
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Part-Invoice dialog — bill a quotation in slices with a per-line
              Approved %. Backend enforces cumulative-% ≤ 100 per line. All
              part invoices share the base number with a numeric suffix
              (INV-0105-1, -2, -3 …). Advance vouchers on the quotation can
              be applied per-invoice up to whatever hasn't been used yet. */}

          {/* ========================================================= */}
          {/* Payment Ledger — consolidated view of every invoice +      */}
          {/* receipt tied to this quotation.                            */}
          {/* ========================================================= */}
          <Dialog
            open={ledgerDialog.open}
            onOpenChange={(o) => { if (!o) setLedgerDialog({ open: false, loading: false, data: null }); }}
          >
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="payment-ledger-dialog">
              <DialogHeader>
                <DialogTitle>Payment Ledger</DialogTitle>
                <DialogDescription>
                  Every invoice, part-invoice and receipt raised against this quotation — with the running balance so you can see what&apos;s billed, what&apos;s received, and what&apos;s still pending.
                </DialogDescription>
              </DialogHeader>

              {ledgerDialog.loading || !ledgerDialog.data ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Loading ledger…</div>
              ) : (() => {
                const d = ledgerDialog.data;
                const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const pillCls = (ps) => ({
                  paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
                  partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
                  partially_paid: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
                  unpaid: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
                }[ps] || 'bg-slate-100 text-slate-800');
                return (
                  <div className="space-y-4">
                    {/* Header — Quotation identity */}
                    <div className="rounded-lg border bg-muted/30 p-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Quotation</div>
                        <div className="font-semibold text-sm">{d.quotation.document_number} · {d.quotation.customer_name}</div>
                        <div className="text-[11px] text-muted-foreground">Dated {d.quotation.date} · Total {fmt(d.quotation.total)}</div>
                      </div>
                      <span className={`text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${pillCls(d.quotation.status)}`}>
                        {d.quotation.status || 'draft'}
                      </span>
                    </div>

                    {/* Summary KPI strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ledger-summary-strip">
                      <div className="rounded-md border bg-background p-2.5">
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Quotation Total</div>
                        <div className="text-lg font-bold font-mono">{fmt(d.summary.quotation_total)}</div>
                      </div>
                      <div className="rounded-md border bg-background p-2.5">
                        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Total Invoiced</div>
                        <div className="text-lg font-bold font-mono">{fmt(d.summary.total_invoiced)}</div>
                        {d.summary.uninvoiced_scope > 0.01 && (
                          <div className="text-[10.5px] text-amber-700 dark:text-amber-400">Uninvoiced: {fmt(d.summary.uninvoiced_scope)}</div>
                        )}
                      </div>
                      <div className="rounded-md border bg-emerald-50/60 dark:bg-emerald-900/20 p-2.5">
                        <div className="text-[10.5px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Received</div>
                        <div className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-300">{fmt(d.summary.total_received)}</div>
                        <div className="text-[10.5px] text-muted-foreground">{d.summary.receipt_count} receipt(s)</div>
                      </div>
                      <div className="rounded-md border bg-rose-50/60 dark:bg-rose-900/20 p-2.5">
                        <div className="text-[10.5px] uppercase tracking-wider text-rose-700 dark:text-rose-400">Balance Pending</div>
                        <div className="text-lg font-bold font-mono text-rose-700 dark:text-rose-300">{fmt(d.summary.total_balance)}</div>
                      </div>
                    </div>

                    {/* Invoices table */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Invoices ({d.summary.invoice_count})</div>
                      <div className="rounded-md border overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-left px-2 py-1.5">Invoice</th>
                              <th className="text-left px-2 py-1.5">Type</th>
                              <th className="text-left px-2 py-1.5">Date</th>
                              <th className="text-right px-2 py-1.5">Total</th>
                              <th className="text-right px-2 py-1.5">Received</th>
                              <th className="text-right px-2 py-1.5">Balance</th>
                              <th className="text-center px-2 py-1.5">Status</th>
                            </tr>
                          </thead>
                          <tbody data-testid="ledger-invoices-tbody">
                            {d.invoices.length === 0 ? (
                              <tr><td colSpan={7} className="text-center text-muted-foreground py-3 italic">No invoices raised yet.</td></tr>
                            ) : d.invoices.map((inv) => (
                              <tr key={inv.id} className="border-t hover:bg-muted/20">
                                <td className="px-2 py-1.5 font-mono font-semibold">{inv.document_number}</td>
                                <td className="px-2 py-1.5">
                                  {inv.is_part_invoice ? (
                                    <span className="text-[10.5px] font-semibold text-indigo-700 dark:text-indigo-400">Part #{inv.part_sequence}</span>
                                  ) : (
                                    <span className="text-[10.5px] text-muted-foreground">Full</span>
                                  )}
                                  {inv.invoice_type === 'proforma_without_tax' && <span className="ml-1 text-[9.5px] rounded bg-slate-100 text-slate-700 px-1">no VAT</span>}
                                  {inv.invoice_type === 'proforma_invoice' && <span className="ml-1 text-[9.5px] rounded bg-slate-100 text-slate-700 px-1">proforma</span>}
                                </td>
                                <td className="px-2 py-1.5 text-muted-foreground">{inv.date}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{fmt(inv.total)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmt(inv.amount_paid)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-rose-700 dark:text-rose-400">{fmt(inv.balance)}</td>
                                <td className="px-2 py-1.5 text-center">
                                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${pillCls(inv.payment_status)}`}>{inv.payment_status || 'unpaid'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {d.invoices.length > 0 && (
                            <tfoot className="bg-muted/40 font-semibold">
                              <tr>
                                <td colSpan={3} className="px-2 py-1.5 text-right">Totals</td>
                                <td className="px-2 py-1.5 text-right font-mono">{fmt(d.summary.total_invoiced)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmt(d.summary.total_received)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-rose-700 dark:text-rose-400">{fmt(d.summary.total_balance)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>

                    {/* Receipts table */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Receipts ({d.summary.receipt_count})</div>
                      <div className="rounded-md border overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-left px-2 py-1.5">Voucher</th>
                              <th className="text-left px-2 py-1.5">Date</th>
                              <th className="text-left px-2 py-1.5">Against Invoice</th>
                              <th className="text-left px-2 py-1.5">Received From</th>
                              <th className="text-right px-2 py-1.5">Amount</th>
                            </tr>
                          </thead>
                          <tbody data-testid="ledger-receipts-tbody">
                            {d.receipts.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted-foreground py-3 italic">No receipts recorded yet.</td></tr>
                            ) : d.receipts.map((r) => (
                              <tr key={r.id} className="border-t hover:bg-muted/20">
                                <td className="px-2 py-1.5 font-mono font-semibold">{r.voucher_number}</td>
                                <td className="px-2 py-1.5 text-muted-foreground">{r.date}</td>
                                <td className="px-2 py-1.5 font-mono text-indigo-700 dark:text-indigo-400">{r.linked_invoice_number || '—'}</td>
                                <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[220px]">{r.received_from || '—'}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmt(r.total_amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="text-[10.5px] text-muted-foreground italic">
                      Balance = Total Invoiced − Total Received. Receipts are counted from the live Receipt Voucher table (not stored on the invoice), so this ledger self-heals against any drift.
                    </div>
                  </div>
                );
              })()}

              <DialogFooter>
                <Button variant="outline" onClick={() => setLedgerDialog({ open: false, loading: false, data: null })} data-testid="ledger-close">
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ========================================================= */}
          {/* Create Part Invoice — this dialog lets the user bill a    */}
          {/* Quotation across multiple part invoices — every line has    */}
          {/* an "Approved %" input and the backend enforces cumulative   */}
          {/* progress ≤ 100 %. Once a part invoice is created, all      */}
          {/* part invoices share the base number with a numeric suffix   */}
          {/* (INV-0105-1, -2, -3 …). Advance vouchers on the quotation   */}
          {/* can be applied per-invoice up to whatever hasn't been used. */}
          {/* ========================================================= */}
          <Dialog
            open={partInvDialog.open}
            onOpenChange={(o) => { if (!o) setPartInvDialog({ open: false, loading: false, submitting: false, preview: null, form: null }); }}
          >
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="part-invoice-dialog">
              <DialogHeader>
                <DialogTitle>Create Part Invoice</DialogTitle>
              </DialogHeader>

              {partInvDialog.loading || !partInvDialog.preview || !partInvDialog.form ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Loading preview…</div>
              ) : (
                <div className="space-y-4">
                  {/* Full-conversion warning — the quote was already
                      converted via the regular "Create Invoice" flow so
                      every line is 100 % billed. Show the linked invoice
                      number and how to unlock. */}
                  {partInvDialog.preview.fully_converted_via_full_invoice && (
                    <div className="rounded-md border border-red-300 bg-red-50/70 dark:bg-red-900/20 dark:border-red-800 px-3 py-2.5 text-[13px] leading-snug text-red-900 dark:text-red-200" data-testid="part-inv-full-conversion-warning">
                      <div className="font-semibold">This quotation is already fully invoiced.</div>
                      <div className="mt-0.5">
                        It was converted to invoice{' '}
                        <span className="font-mono font-semibold">{partInvDialog.preview.linked_full_invoice_number || '(unknown)'}</span>{' '}
                        via <em>Create Invoice</em> (not part-invoicing). To bill this scope in slices instead, first <strong>void or delete</strong> that invoice, then reopen this dialog.
                      </div>
                    </div>
                  )}
                  {/* Header — quotation + numbering + advance pool */}
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-md border bg-muted/30 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Quotation</div>
                      <div className="font-mono font-semibold">{partInvDialog.preview.quotation_number}</div>
                      <div className="text-muted-foreground">{partInvDialog.preview.customer_name}</div>
                      <div className="text-xs text-muted-foreground">Total: <span className="font-mono">{Number(partInvDialog.preview.quotation_total).toFixed(2)}</span></div>
                    </div>
                    <div className="rounded-md border bg-indigo-50/60 dark:bg-indigo-900/20 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">This Invoice</div>
                      <div className="font-mono font-semibold text-indigo-700 dark:text-indigo-300" data-testid="part-inv-next-no">
                        {partInvDialog.preview.next_invoice_number}
                      </div>
                      <div className="text-xs text-muted-foreground">Part {partInvDialog.preview.next_part_sequence} of series</div>
                      {partInvDialog.preview.existing_part_invoices.length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Previous: {partInvDialog.preview.existing_part_invoices.map((p) => p.document_number).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="rounded-md border bg-emerald-50/60 dark:bg-emerald-900/20 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Advance Pool</div>
                      <div className="text-sm">
                        Received: <span className="font-mono">{Number(partInvDialog.preview.advance_pool).toFixed(2)}</span>
                      </div>
                      <div className="text-sm">
                        Already applied: <span className="font-mono">{Number(partInvDialog.preview.advance_already_applied).toFixed(2)}</span>
                      </div>
                      <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                        Available: <span className="font-mono">{Number(partInvDialog.preview.advance_available).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* ---- Invoice-type + billing-mode toggle bar (Feb 2026) ---- */}
                  <div className="rounded-md border bg-muted/20 p-3 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Invoice setup</div>
                      {/* One-click "Advance Invoice" preset — flips type to
                          Proforma-Without-Tax + Lumpsum mode + description
                          prefilled. User then picks the % from the quick
                          picker below. */}
                      <button
                        type="button"
                        onClick={() => setPartInvDialog((s) => ({
                          ...s,
                          form: {
                            ...s.form,
                            invoiceType: 'proforma_without_tax',
                            billingMode: 'lumpsum',
                            lumpsumDescription: s.form.lumpsumDescription
                              || `Advance payment — ${partInvDialog.preview.quotation_number}`,
                            terms: s.form.terms
                              || 'Advance to be paid before commencement of work. This proforma is not a tax invoice.',
                          },
                        }))}
                        className="text-[11px] font-semibold rounded-full border border-indigo-500 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 px-3 py-1 transition"
                        data-testid="part-inv-preset-advance"
                        title="Set up a typical advance-payment proforma in one click"
                      >
                        ✨ Use “Advance Invoice” preset
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Invoice Type</label>
                      <select
                        className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={partInvDialog.form.invoiceType}
                        onChange={(e) => setPartInvDialog((s) => ({
                          ...s,
                          form: { ...s.form, invoiceType: e.target.value },
                        }))}
                        data-testid="part-inv-type"
                      >
                        <option value="tax_invoice">Tax Invoice (VAT applied)</option>
                        <option value="proforma_invoice">Proforma Invoice (with tax)</option>
                        <option value="proforma_without_tax">Proforma Invoice — WITHOUT tax</option>
                      </select>
                      <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
                        {partInvDialog.form.invoiceType === 'proforma_without_tax'
                          ? 'Total will equal the sum of net line amounts — no VAT will be added, even if the source Quotation carried tax.'
                          : partInvDialog.form.invoiceType === 'proforma_invoice'
                          ? 'Proforma is not a legal tax document. No QR-verification on the PDF.'
                          : 'Standard tax invoice — VAT is computed from each line and shown on the PDF.'}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Billing Mode</label>
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPartInvDialog((s) => ({ ...s, form: { ...s.form, billingMode: 'per_line' } }))}
                          data-testid="part-inv-mode-perline"
                          className={`h-9 rounded-md border text-xs font-semibold transition ${partInvDialog.form.billingMode === 'per_line' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted'}`}
                        >Per-Line %</button>
                        <button
                          type="button"
                          onClick={() => setPartInvDialog((s) => ({ ...s, form: { ...s.form, billingMode: 'lumpsum' } }))}
                          data-testid="part-inv-mode-lumpsum"
                          className={`h-9 rounded-md border text-xs font-semibold transition ${partInvDialog.form.billingMode === 'lumpsum' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted'}`}
                        >Lumpsum Amount</button>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground leading-snug">
                        {partInvDialog.form.billingMode === 'lumpsum'
                          ? 'Bill an arbitrary milestone amount for this round. Advance & previous payments will be adjusted against the amount you enter.'
                          : 'Enter Approved % per line below — this Invoice total re-computes as you type.'}
                      </div>
                    </div>
                    </div>
                  </div>

                  {/* ---- Lumpsum amount input (only when Lumpsum mode) ---- */}
                  {partInvDialog.form.billingMode === 'lumpsum' && (
                    <div className="rounded-md border bg-indigo-50/60 dark:bg-indigo-900/20 p-3 space-y-3">
                      {/* Advance / milestone % quick-picker — auto-fills the
                          Lumpsum amount from the QUOTE total. Great for the
                          typical "raise a 30 % advance proforma" flow the
                          user asked for on QT-47. */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block">Quick pick — % of quotation total ({Number(partInvDialog.preview.quotation_total || 0).toLocaleString()})</label>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          {[10, 20, 25, 30, 40, 50, 70, 100].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => {
                                const qt = Number(partInvDialog.preview.quotation_total || 0);
                                const amt = Math.round((qt * pct / 100) * 100) / 100;
                                setPartInvDialog((s) => ({
                                  ...s,
                                  form: {
                                    ...s.form,
                                    lumpsumAmount: String(amt),
                                    lumpsumDescription: s.form.lumpsumDescription
                                      || `Advance payment — ${pct}% of ${partInvDialog.preview.quotation_number}`,
                                  },
                                }));
                              }}
                              className="text-[11px] font-semibold rounded-full border bg-background hover:bg-indigo-100 dark:hover:bg-indigo-800/30 px-2.5 py-1 transition"
                              data-testid={`part-inv-pct-${pct}`}
                            >{pct}%</button>
                          ))}
                          <span className="text-[10px] text-muted-foreground ml-2">Click to fill the amount below</span>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Lumpsum Amount ({partInvDialog.form.invoiceType === 'proforma_without_tax' ? 'no VAT' : 'value as-is'})</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={partInvDialog.form.lumpsumAmount}
                            onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, lumpsumAmount: e.target.value } }))}
                            placeholder="e.g. 100000"
                            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm font-mono"
                            data-testid="part-inv-lumpsum-amount"
                          />
                          {partInvDialog.form.lumpsumAmount && Number(partInvDialog.preview.quotation_total) > 0 && (
                            <div className="mt-1 text-[10.5px] text-muted-foreground">
                              = {((Number(partInvDialog.form.lumpsumAmount) / Number(partInvDialog.preview.quotation_total)) * 100).toFixed(2)}% of quotation
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Line description (optional)</label>
                          <input
                            type="text"
                            value={partInvDialog.form.lumpsumDescription}
                            onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, lumpsumDescription: e.target.value } }))}
                            placeholder={`Advance payment — % of ${partInvDialog.preview.quotation_number}`}
                            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="part-inv-lumpsum-desc"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Line items table — % + apply mode + editable fields */}
                  <div className={partInvDialog.form.billingMode === 'lumpsum' ? 'opacity-50 pointer-events-none' : ''}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                        Line Items — Approved % this round
                        {partInvDialog.form.billingMode === 'lumpsum' && <span className="ml-2 italic normal-case font-normal">(disabled while in Lumpsum mode)</span>}
                      </div>
                      <Button variant="outline" size="sm" onClick={addPartInvExtraLine} data-testid="part-inv-add-line">
                        + Add Extra Line
                      </Button>
                    </div>
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-2 py-2 text-left">#</th>
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-right">Qty</th>
                            <th className="px-2 py-2 text-right">Rate</th>
                            <th className="px-2 py-2 text-right">Tax %</th>
                            <th className="px-2 py-2 text-center">Already&nbsp;/&nbsp;Bal</th>
                            <th className="px-2 py-2 text-right">Approved %</th>
                            <th className="px-2 py-2 text-center">Apply</th>
                            <th className="px-2 py-2 text-right">This Round</th>
                            <th className="px-2 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {partInvDialog.form.lines.map((l, idx) => {
                            const qty = parseFloat(l.quantity) || 0;
                            const price = parseFloat(l.unit_price) || 0;
                            const pct = Math.max(0, Math.min(parseFloat(l.applied_percentage) || 0, 100)) / 100;
                            const rate = (parseFloat(l.tax_rate) || 0) / 100;
                            const base = l.apply_mode === 'quantity' ? qty * pct * price : qty * price * pct;
                            const thisRound = base + base * rate;
                            const overBalance = l._from_quote && (parseFloat(l.applied_percentage) || 0) > l._balance + 1e-3;
                            return (
                              <tr key={idx} className={overBalance ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                                <td className="px-2 py-1.5 text-muted-foreground">{idx + 1}</td>
                                <td className="px-2 py-1.5">
                                  <input
                                    className="w-56 h-8 rounded border border-input bg-background px-1.5 text-xs"
                                    value={l.description}
                                    onChange={(e) => updatePartInvLine(idx, { description: e.target.value })}
                                    data-testid={`part-inv-desc-${idx}`}
                                  />
                                  {(parseFloat(l.applied_percentage) || 0) <= 0 && (
                                    <div className="text-[10px] font-semibold text-muted-foreground bg-muted/70 dark:bg-muted/40 border border-border rounded px-1.5 py-0.5 mt-1 inline-block" data-testid={`part-inv-skip-badge-${idx}`}>
                                      Won&apos;t be billed (0 %)
                                    </div>
                                  )}
                                  {l._from_quote ? (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">From quotation line #{l.quotation_line_index + 1}</div>
                                  ) : (
                                    <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">Extra line (not on quotation)</div>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number"
                                    className="w-16 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.quantity}
                                    onChange={(e) => updatePartInvLine(idx, { quantity: e.target.value })}
                                    data-testid={`part-inv-qty-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number"
                                    className="w-20 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.unit_price}
                                    onChange={(e) => updatePartInvLine(idx, { unit_price: e.target.value })}
                                    data-testid={`part-inv-rate-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number"
                                    className="w-14 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.tax_rate}
                                    onChange={(e) => updatePartInvLine(idx, { tax_rate: e.target.value })}
                                    data-testid={`part-inv-tax-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5 text-center whitespace-nowrap">
                                  {l._from_quote ? (
                                    <div className="text-[11px] leading-tight">
                                      <div className="text-muted-foreground">{Number(l._already).toFixed(2)}%</div>
                                      <div className={l._balance <= 0 ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>
                                        {Number(l._balance).toFixed(2)}% left
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    className={`w-20 h-8 rounded border px-1.5 text-xs text-right ${overBalance ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-input bg-background'}`}
                                    value={l.applied_percentage}
                                    onChange={(e) => updatePartInvLine(idx, { applied_percentage: e.target.value })}
                                    data-testid={`part-inv-pct-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <select
                                    className="h-8 rounded border border-input bg-background px-1 text-xs"
                                    value={l.apply_mode}
                                    onChange={(e) => updatePartInvLine(idx, { apply_mode: e.target.value })}
                                    data-testid={`part-inv-mode-${idx}`}
                                  >
                                    <option value="amount">Amount</option>
                                    <option value="quantity">Qty</option>
                                  </select>
                                </td>
                                <td className="px-2 py-1.5 text-right font-mono">{thisRound.toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-center">
                                  {!l._from_quote && (
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => removePartInvLine(idx)} data-testid={`part-inv-remove-${idx}`}>
                                      ×
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-1.5 text-[11px] text-muted-foreground">
                      <strong>Amount</strong> mode scales the line total by %. <strong>Qty</strong> mode scales the quantity while keeping the unit rate. Tax is computed after the % is applied.
                    </div>
                  </div>

                  {/* History block — earlier part invoices on this quotation */}
                  {partInvDialog.preview.existing_part_invoices.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                        Earlier Part Invoices
                      </div>
                      <div className="rounded-md border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="px-2 py-1.5 text-left">#</th>
                              <th className="px-2 py-1.5 text-left">Number</th>
                              <th className="px-2 py-1.5 text-left">Date</th>
                              <th className="px-2 py-1.5 text-right">Total</th>
                              <th className="px-2 py-1.5 text-right">Advance</th>
                              <th className="px-2 py-1.5 text-left">Payment</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {partInvDialog.preview.existing_part_invoices.map((p) => (
                              <tr key={p.id}>
                                <td className="px-2 py-1">{p.part_sequence}</td>
                                <td className="px-2 py-1 font-mono">{p.document_number}</td>
                                <td className="px-2 py-1 text-muted-foreground">{p.date}</td>
                                <td className="px-2 py-1 text-right font-mono">{Number(p.total).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right font-mono">{Number(p.advance_payment).toFixed(2)}</td>
                                <td className="px-2 py-1 text-muted-foreground">{p.payment_status || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Bottom: due date, advance to apply, notes, live totals */}
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Due Date</label>
                      <input
                        type="date"
                        value={partInvDialog.form.dueDate}
                        onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, dueDate: e.target.value } }))}
                        className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="part-inv-due-date"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        Advance to Apply (max {Number(partInvDialog.preview.advance_available).toFixed(2)})
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={partInvDialog.preview.advance_available}
                        value={partInvDialog.form.advance}
                        onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, advance: e.target.value } }))}
                        className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        disabled={partInvDialog.preview.advance_available <= 0}
                        data-testid="part-inv-advance"
                      />
                    </div>
                    <div className="rounded-md border bg-muted/30 p-2 text-xs space-y-0.5">
                      <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{partInvComputedTotals.subtotal.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Tax</span><span className="font-mono">{partInvComputedTotals.tax.toFixed(2)}</span></div>
                      <div className="flex justify-between border-t pt-0.5 mt-0.5 font-semibold" data-testid="part-inv-total-row">
                        <span>This Invoice Total</span>
                        <span className="font-mono">{partInvComputedTotals.total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-700 dark:text-emerald-400">
                        <span>− Advance applied</span>
                        <span className="font-mono">{(parseFloat(partInvDialog.form.advance) || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sky-700 dark:text-sky-400">
                        <span>Billed so far (previous parts)</span>
                        <span className="font-mono">
                          {(partInvDialog.preview.existing_part_invoices || [])
                            .reduce((a, p) => a + Number(p.total || 0), 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-0.5 mt-0.5 font-semibold" data-testid="part-inv-payable-row">
                        <span>Payable Now</span>
                        <span className="font-mono">{Math.max(0, partInvComputedTotals.total - (parseFloat(partInvDialog.form.advance) || 0)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-0.5 mt-0.5 text-muted-foreground italic" data-testid="part-inv-balance-after-row">
                        <span>Balance after this invoice</span>
                        <span className="font-mono">
                          {Math.max(0,
                            Number(partInvDialog.preview.quotation_total || 0)
                            - (partInvDialog.preview.existing_part_invoices || []).reduce((a, p) => a + Number(p.total || 0), 0)
                            - partInvComputedTotals.total
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Notes</label>
                      <Textarea
                        rows={2}
                        value={partInvDialog.form.notes}
                        onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, notes: e.target.value } }))}
                        placeholder={`Part ${partInvDialog.preview.next_part_sequence} of quotation ${partInvDialog.preview.quotation_number}`}
                        data-testid="part-inv-notes"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Terms &amp; Conditions</label>
                      <Textarea
                        rows={2}
                        value={partInvDialog.form.terms}
                        onChange={(e) => setPartInvDialog((s) => ({ ...s, form: { ...s.form, terms: e.target.value } }))}
                        data-testid="part-inv-terms"
                      />
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setPartInvDialog({ open: false, loading: false, submitting: false, preview: null, form: null })}>
                  Cancel
                </Button>
                <Button
                  onClick={submitPartInvoice}
                  disabled={partInvDialog.loading || partInvDialog.submitting || !partInvDialog.preview || !partInvDialog.form}
                  data-testid="part-inv-submit-btn"
                >
                  {partInvDialog.submitting ? 'Creating…' : `Create ${partInvDialog.preview?.next_invoice_number || 'Part Invoice'}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Project Part-Invoice dialog — Invoices tab entry point (no quotation
              required). Round 1 lets the user manually enter Original Qty/Rate
              + Approved Qty per line. Round N loads the anchor scope from the
              chain and only allows billing up to each line's balance qty.
              Chain identified by (customer + project reference). */}
          <Dialog
            open={projPartDialog.open}
            onOpenChange={(o) => { if (!o) setProjPartDialog({ open: false, step: 'setup', loading: false, submitting: false, customerId: '', projectReference: '', preview: null, form: null }); }}
          >
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="proj-part-invoice-dialog">
              <DialogHeader>
                <DialogTitle>
                  {projPartDialog.step === 'setup' ? 'New Project Part Invoice' : (
                    projPartDialog.preview?.is_first_round
                      ? `Round 1 — enter project scope & approved qty`
                      : `Round ${projPartDialog.preview?.next_part_sequence} — bill remaining approved qty`
                  )}
                </DialogTitle>
              </DialogHeader>

              {/* STEP 1: pick customer + project reference */}
              {projPartDialog.step === 'setup' && (
                <div className="space-y-4">
                  <DraftRestoreBanner
                    visible={projPartDraft.hasDraft}
                    savedAt={projPartDraft.draftSavedAt}
                    label="part invoice"
                    onRestore={restoreProjPartDraft}
                    onDiscard={projPartDraft.discard}
                  />
                  <div className="text-sm text-muted-foreground">
                    Pick the customer and enter a <strong>Project Reference</strong>. If any part invoices already exist for this pair, the next round will load with balance-qty already computed; otherwise this becomes round 1 and you&apos;ll enter the base scope manually.
                  </div>
                  <div className="rounded-md border border-amber-200 bg-amber-50/70 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2 text-[12px] leading-snug text-amber-900 dark:text-amber-200">
                    <strong>When to use this flow</strong> — pick this if the customer / project has <em>no</em> Quotation in the system, or you want to bill scope that wasn&apos;t priced upfront. If you already have a Quotation (e.g. <span className="font-mono">QT-47 HUDA</span>), go to the <strong>Quotations</strong> tab, open that quote and use <em>Actions → Create Part Invoice…</em> — that flow inherits the quote lines and keeps the two invoice chains cleanly separate.
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Customer</label>
                    <Select
                      value={projPartDialog.customerId}
                      onValueChange={(v) => setProjPartDialog((s) => ({ ...s, customerId: v }))}
                    >
                      <SelectTrigger className="w-full mt-1" data-testid="proj-part-customer">
                        <SelectValue placeholder="Select a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {(contacts || []).filter(c => !c.type || ['customer', 'both'].includes((c.type || '').toLowerCase())).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company_name || c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Project Reference</label>
                    <input
                      value={projPartDialog.projectReference}
                      onChange={(e) => setProjPartDialog((s) => ({ ...s, projectReference: e.target.value }))}
                      placeholder="e.g. JOB-2026-014 or Building-A Villa 12"
                      className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                      data-testid="proj-part-reference"
                    />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Use the same reference on every round for this project so cumulative qty is tracked correctly.
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setProjPartDialog((s) => ({ ...s, open: false }))}>Cancel</Button>
                    <Button
                      onClick={loadProjPartPreview}
                      disabled={projPartDialog.loading || !projPartDialog.customerId || !projPartDialog.projectReference.trim()}
                      data-testid="proj-part-continue-btn"
                    >
                      {projPartDialog.loading ? 'Loading…' : 'Continue'}
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* STEP 2: line table + submit */}
              {projPartDialog.step === 'form' && projPartDialog.preview && projPartDialog.form && (
                <div className="space-y-4">
                  {/* Header cards */}
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-md border bg-muted/30 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Customer</div>
                      <div className="font-semibold">{projPartDialog.preview.customer_name}</div>
                      <div className="text-xs text-muted-foreground">Project: <span className="font-mono">{projPartDialog.preview.project_reference}</span></div>
                    </div>
                    <div className="rounded-md border bg-indigo-50/60 dark:bg-indigo-900/20 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">This Invoice</div>
                      <div className="font-mono font-semibold text-indigo-700 dark:text-indigo-300" data-testid="proj-part-next-no">
                        {projPartDialog.preview.next_invoice_number}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Round {projPartDialog.preview.next_part_sequence}
                        {projPartDialog.preview.chain_length > 0 && ` (${projPartDialog.preview.chain_length} prior)`}
                      </div>
                    </div>
                    <div className="rounded-md border bg-emerald-50/60 dark:bg-emerald-900/20 p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Progress</div>
                      {projPartDialog.preview.is_first_round ? (
                        <div className="text-sm">Fresh project — you&apos;re setting the scope now.</div>
                      ) : (
                        <div className="text-sm">
                          {projPartDialog.preview.line_progress.length} scope line{projPartDialog.preview.line_progress.length !== 1 ? 's' : ''} anchored — original qty & rate come from{' '}
                          <span className="font-mono">{projPartDialog.preview.line_progress[0]?.first_seen_invoice || projPartDialog.preview.base_invoice_number + '-1'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Line items */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                        {projPartDialog.preview.is_first_round ? 'Enter scope lines' : 'Bill approved qty this round'}
                      </div>
                      <Button variant="outline" size="sm" onClick={addProjPartExtraLine} data-testid="proj-part-add-line">
                        + Add Line
                      </Button>
                    </div>
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-2 py-2 text-left w-6">#</th>
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-right">Original Qty</th>
                            <th className="px-2 py-2 text-right">Original Rate</th>
                            <th className="px-2 py-2 text-center">Already / Bal</th>
                            <th className="px-2 py-2 text-right">Approved Qty</th>
                            <th className="px-2 py-2 text-right">Approved %</th>
                            <th className="px-2 py-2 text-right">Tax %</th>
                            <th className="px-2 py-2 text-right">This Round</th>
                            <th className="px-2 py-2 w-6"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {projPartDialog.form.lines.map((l, idx) => {
                            const approved = parseFloat(l.approved_quantity) || 0;
                            const price = parseFloat(l.original_unit_price) || 0;
                            const rate = (parseFloat(l.tax_rate) || 0) / 100;
                            const thisRound = approved * price * (1 + rate);
                            const overBal = l._from_chain && approved > l._balance_quantity + 1e-3;
                            const rateDeviated = l._from_chain && Math.abs(price - l._anchor_original_unit_price) > 1e-3;
                            const qtyDeviated = l._from_chain && Math.abs(parseFloat(l.original_quantity) - l._anchor_original_quantity) > 1e-3;
                            return (
                              <tr key={idx} className={overBal ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                                <td className="px-2 py-1.5 text-muted-foreground">{idx + 1}</td>
                                <td className="px-2 py-1.5">
                                  <input
                                    className={`w-56 h-8 rounded border px-1.5 text-xs ${l._from_chain ? 'border-input bg-muted/40' : 'border-input bg-background'}`}
                                    value={l.description}
                                    onChange={(e) => updateProjPartLine(idx, { description: e.target.value })}
                                    disabled={l._from_chain}
                                    data-testid={`proj-part-desc-${idx}`}
                                  />
                                  {(parseFloat(l.approved_quantity) || 0) <= 0 && (
                                    <div className="text-[10px] font-semibold text-muted-foreground bg-muted/70 dark:bg-muted/40 border border-border rounded px-1.5 py-0.5 mt-1 inline-block" data-testid={`proj-part-skip-badge-${idx}`}>
                                      Won&apos;t be billed (0 qty)
                                    </div>
                                  )}
                                  {l._from_chain ? (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Anchored from round 1</div>
                                  ) : (
                                    <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                                      {projPartDialog.preview.is_first_round ? 'Scope line' : 'Extra line (new anchor)'}
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number" step="0.01" min="0"
                                    className="w-20 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.original_quantity}
                                    onChange={(e) => updateProjPartLine(idx, { original_quantity: e.target.value })}
                                    data-testid={`proj-part-oqty-${idx}`}
                                  />
                                  {qtyDeviated && (
                                    <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">
                                      base was: {l._anchor_original_quantity}
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number" step="0.01" min="0"
                                    className="w-20 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.original_unit_price}
                                    onChange={(e) => updateProjPartLine(idx, { original_unit_price: e.target.value })}
                                    data-testid={`proj-part-orate-${idx}`}
                                  />
                                  {rateDeviated && (
                                    <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">
                                      base was: {l._anchor_original_unit_price}
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-center whitespace-nowrap">
                                  {l._from_chain ? (
                                    <div className="text-[11px] leading-tight">
                                      <div className="text-muted-foreground">{Number(l._already_invoiced_quantity).toFixed(2)}</div>
                                      <div className={l._balance_quantity <= 0 ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>
                                        bal {Number(l._balance_quantity).toFixed(2)}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number" step="0.01" min="0"
                                    max={l._from_chain ? l._balance_quantity : l.original_quantity}
                                    className={`w-20 h-8 rounded border px-1.5 text-xs text-right ${overBal ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-input bg-background'}`}
                                    value={l.approved_quantity}
                                    onChange={(e) => updateProjPartLine(idx, { approved_quantity: e.target.value })}
                                    data-testid={`proj-part-aqty-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number" step="0.01" min="0" max="100"
                                    className="w-16 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.approved_percentage}
                                    onChange={(e) => updateProjPartLine(idx, { approved_percentage: e.target.value })}
                                    data-testid={`proj-part-apct-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="number" step="0.01" min="0"
                                    className="w-14 h-8 rounded border border-input bg-background px-1.5 text-xs text-right"
                                    value={l.tax_rate}
                                    onChange={(e) => updateProjPartLine(idx, { tax_rate: e.target.value })}
                                    data-testid={`proj-part-tax-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5 text-right font-mono">{thisRound.toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-center">
                                  {!l._from_chain && (
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => removeProjPartLine(idx)} data-testid={`proj-part-remove-${idx}`}>
                                      ×
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-1.5 text-[11px] text-muted-foreground">
                      Edit either <strong>Approved Qty</strong> or <strong>Approved %</strong> — the other auto-updates. On round 2+, changing Original Qty/Rate is allowed but the balance ceiling stays anchored to round 1 and any deviation is recorded on the invoice audit trail.
                    </div>
                  </div>

                  {/* Earlier parts */}
                  {projPartDialog.preview.existing_part_invoices?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                        Earlier Part Invoices
                      </div>
                      <div className="rounded-md border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="px-2 py-1.5 text-left">Round</th>
                              <th className="px-2 py-1.5 text-left">Number</th>
                              <th className="px-2 py-1.5 text-left">Date</th>
                              <th className="px-2 py-1.5 text-right">Total</th>
                              <th className="px-2 py-1.5 text-left">Payment</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {projPartDialog.preview.existing_part_invoices.map((p) => (
                              <tr key={p.id}>
                                <td className="px-2 py-1">{p.part_sequence}</td>
                                <td className="px-2 py-1 font-mono">{p.document_number}</td>
                                <td className="px-2 py-1 text-muted-foreground">{p.date}</td>
                                <td className="px-2 py-1 text-right font-mono">{Number(p.total).toFixed(2)}</td>
                                <td className="px-2 py-1 text-muted-foreground">{p.payment_status || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Bottom fields + totals */}
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Due Date</label>
                      <input
                        type="date"
                        value={projPartDialog.form.dueDate}
                        onChange={(e) => setProjPartDialog((s) => ({ ...s, form: { ...s.form, dueDate: e.target.value } }))}
                        className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="proj-part-due-date"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Advance to Apply</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={projPartDialog.form.advance}
                        onChange={(e) => setProjPartDialog((s) => ({ ...s, form: { ...s.form, advance: e.target.value } }))}
                        className="w-full mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="proj-part-advance"
                      />
                    </div>
                    <div className="rounded-md border bg-muted/30 p-2 text-xs space-y-0.5">
                      <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{projPartTotals.subtotal.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Tax</span><span className="font-mono">{projPartTotals.tax.toFixed(2)}</span></div>
                      <div className="flex justify-between border-t pt-0.5 mt-0.5 font-semibold"><span>Total</span><span className="font-mono">{projPartTotals.total.toFixed(2)}</span></div>
                      <div className="flex justify-between text-emerald-700 dark:text-emerald-400"><span>− Advance</span><span className="font-mono">{(parseFloat(projPartDialog.form.advance) || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between border-t pt-0.5 mt-0.5 font-semibold"><span>Payable Now</span><span className="font-mono">{Math.max(0, projPartTotals.total - (parseFloat(projPartDialog.form.advance) || 0)).toFixed(2)}</span></div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Notes</label>
                      <Textarea
                        rows={2}
                        value={projPartDialog.form.notes}
                        onChange={(e) => setProjPartDialog((s) => ({ ...s, form: { ...s.form, notes: e.target.value } }))}
                        placeholder={`Round ${projPartDialog.preview.next_part_sequence} — ${projPartDialog.preview.project_reference}`}
                        data-testid="proj-part-notes"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Terms &amp; Conditions</label>
                      <Textarea
                        rows={2}
                        value={projPartDialog.form.terms}
                        onChange={(e) => setProjPartDialog((s) => ({ ...s, form: { ...s.form, terms: e.target.value } }))}
                        data-testid="proj-part-terms"
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setProjPartDialog((s) => ({ ...s, step: 'setup' }))}>← Back</Button>
                    <div className="flex-1"><DraftSavedIndicator savedAt={projPartDraft.lastAutoSavedAt} /></div>
                    <Button
                      onClick={submitProjPart}
                      disabled={projPartDialog.submitting}
                      data-testid="proj-part-submit-btn"
                    >
                      {projPartDialog.submitting ? 'Creating…' : `Create ${projPartDialog.preview?.next_invoice_number}`}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>


          {/* Quotation approval guard — shown when the user picks a quotation
              that hasn't been client-approved yet. Encourages them to update
              the quote's status before raising an invoice. */}
          <AlertDialog
            open={approvalGuard.open}
            onOpenChange={(open) => { if (!open) setApprovalGuard({ open: false, quote: null }); }}
          >
            <AlertDialogContent data-testid="quote-approval-guard">
              <AlertDialogHeader>
                <AlertDialogTitle>Quote not yet approved by client</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      Quotation <strong className="text-foreground">{approvalGuard.quote?.document_number}</strong>
                      {' '}is currently in status{' '}
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                        {approvalGuard.quote?.status?.replace(/_/g, ' ')}
                      </span>.
                    </p>
                    <p>
                      Best practice: confirm the quote is{' '}
                      <strong>Client Approved</strong> or <strong>Agreement Signed</strong>{' '}
                      before raising an invoice. Otherwise you risk billing against a quote
                      the customer hasn't formally accepted.
                    </p>
                    {(QUOTE_TRANSITION_RULES[approvalGuard.quote?.status] || []).length === 0 && (
                      <p className="rounded-md bg-muted/50 p-2 text-xs">
                        ⓘ Direct status change isn't allowed from <strong>{approvalGuard.quote?.status?.replace(/_/g, ' ')}</strong>.
                        Send the quote to the client first (from the quote's row menu),
                        then come back here once the customer confirms.
                      </p>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
                <AlertDialogCancel
                  data-testid="quote-guard-cancel"
                  onClick={() => setApprovalGuard({ open: false, quote: null })}
                >
                  Cancel
                </AlertDialogCancel>
                {(QUOTE_TRANSITION_RULES[approvalGuard.quote?.status] || []).includes('client_approved') && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="quote-guard-mark-approved"
                    onClick={async () => {
                      const updated = await transitionQuoteStatus(approvalGuard.quote, 'client_approved');
                      if (updated) {
                        applyQuotationToInvoice(updated);
                        setApprovalGuard({ open: false, quote: null });
                      }
                    }}
                  >
                    Mark Client Approved &amp; continue
                  </Button>
                )}
                {(QUOTE_TRANSITION_RULES[approvalGuard.quote?.status] || []).includes('agreement_signed') && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="quote-guard-mark-agreement"
                    onClick={async () => {
                      const updated = await transitionQuoteStatus(approvalGuard.quote, 'agreement_signed');
                      if (updated) {
                        applyQuotationToInvoice(updated);
                        setApprovalGuard({ open: false, quote: null });
                      }
                    }}
                  >
                    Mark Agreement Signed &amp; continue
                  </Button>
                )}
                <AlertDialogAction
                  data-testid="quote-guard-continue-anyway"
                  onClick={() => {
                    if (approvalGuard.quote) applyQuotationToInvoice(approvalGuard.quote);
                    setApprovalGuard({ open: false, quote: null });
                  }}
                >
                  Continue anyway
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        <div className="card-tactile rounded-lg p-5" data-testid="sales-card-total-sales">
          <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Total Sales (approved)</p>
          <p className="text-2xl font-heading font-semibold mt-1">{formatCurrency(stats?.total_sales || 0)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Incl. VAT · {formatCurrency(stats?.total_sales_excl_vat || 0)} before VAT · {stats?.posted_invoices || 0} invoice(s)</p>
        </div>
        <div className="card-tactile rounded-lg p-5" data-testid="sales-card-received">
          <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Received</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-emerald-600">{formatCurrency(stats?.total_received || 0)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Outstanding <span className="font-semibold text-rose-600">{formatCurrency(stats?.total_outstanding || 0)}</span> · {stats?.unpaid_invoices || 0} unpaid
          </p>
        </div>
        <div className="card-tactile rounded-lg p-5" data-testid="sales-card-pending">
          <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Pending Approval</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-amber-600">{stats?.pending_invoices || 0}</p>
          <p className="text-[11px] text-muted-foreground mt-1">of {stats?.total_invoices || 0} invoice(s) — not yet in the books</p>
        </div>
        <div className="card-tactile rounded-lg p-5">
          <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Customers</p>
          <p className="text-2xl font-heading font-semibold mt-1">{stats?.total_customers || 0}</p>
        </div>
      </motion.div>

      {/* Tabs for Quotations and Invoices */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Search Bar */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search by invoice #, customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="sales-search"
            />
          </div>
        </div>

        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <Receipt size={16} className="mr-2" />
            Invoices ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="quotations" data-testid="tab-quotations">
            <FileText size={16} className="mr-2" />
            Quotations ({quotations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <DocSummary
            kind="invoices"
            label={`Invoices${invoiceStatusFilter !== 'all' ? ` — ${invoiceStatusFilter.replace(/_/g, ' ')}` : ''}`}
            filters={{
              q: searchTerm,
              ...(invoiceStatusFilter !== 'all' ? { status: invoiceStatusFilter } : {}),
            }}
          />
          {/* Status filter + Show-all-revisions toggle — the status drives
              both the visible list below AND the summary bar above so what
              the user sees is what they print. */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="invoice-filter-bar">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground">Status:</label>
              <select
                value={invoiceStatusFilter}
                onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="invoice-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="sent">Sent</option>
                <option value="sent_to_client">Sent to Client</option>
                <option value="partial">Partially Paid</option>
                <option value="partially_paid">Partially Paid (legacy)</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="on_hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
                <option value="void">Void</option>
              </select>
              {invoiceStatusFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setInvoiceStatusFilter('all')}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="invoice-status-filter-clear"
                >
                  Clear
                </button>
              )}
              {canCreate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openProjPartDialog}
                  className="ml-3 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300"
                  data-testid="open-proj-part-invoice-btn"
                >
                  <FileText size={14} className="mr-1.5" /> New Part Invoice (Project)
                </Button>
              )}
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAllRevisions}
                onChange={(e) => setShowAllRevisions(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-amber-500"
                data-testid="invoice-show-all-revisions-checkbox"
              />
              Show all revisions
              <span className="text-[10px] text-muted-foreground/70">(originals + R1, R2…)</span>
            </label>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Invoice #</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Customer</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden lg:table-cell">Project</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden sm:table-cell">Date</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden md:table-cell">Due</th>
                    <th className="text-right px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Payable</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Status</th>
                    <th className="text-right px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.filter(inv => {
                    // Status filter must match the DocSummary bar above so
                    // list ↔ summary ↔ PDF stay in sync.
                    if (invoiceStatusFilter !== 'all') {
                      if ((inv.status || 'draft').toLowerCase() !== invoiceStatusFilter) return false;
                    }
                    if (!searchTerm) return true;
                    // Multi-token AND search: every whitespace-split token
                    // must appear in at least one searchable field. Fixes the
                    // "ALI Sons" vs "ALI  and Sons" (extra spaces / word in
                    // between) mismatch users hit repeatedly.
                    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
                    const haystack = [
                      inv.document_number, inv.customer_name,
                      inv.project_reference, inv.project_location,
                      inv.reference, inv.po_reference, inv.created_by_name,
                    ].filter(Boolean).join(' ').toLowerCase();
                    return tokens.every(t => haystack.includes(t));
                  }).map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-muted/30 transition-colors" data-testid={`invoice-row-${invoice.id}`}>
                      <td className="px-4 lg:px-6 py-4 font-mono text-sm">
                        {invoice.document_number}
                        {invoice.revision && invoice.revision !== 'R0' && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">{invoice.revision}</span>
                        )}
                        {/* Linked-quotation indicator — resolves the source
                            quotation number from the currently-loaded list
                            (falls back to the raw ID when the quotation isn't
                            in the current window). Chip mirrors the treatment
                            used for revision badges above. */}
                        {(() => {
                          const qId = invoice.quotation_id || invoice.linked_quotation_id;
                          if (!qId) return null;
                          const src = (quotations || []).find(q => q.id === qId);
                          const label = src?.document_number || `Q:${String(qId).slice(0, 6)}`;
                          return (
                            <span
                              className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
                              title={`Linked to quotation ${label}`}
                              data-testid={`invoice-linked-quo-${invoice.id}`}
                            >
                              ↖ {label}
                            </span>
                          );
                        })()}
                        {/* Part-invoice indicator — shows "Part X of BASE"
                            so users can instantly identify progress-billing
                            invoices and their sibling full invoice. */}
                        {invoice.is_part_invoice && invoice.part_sequence && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300"
                            title={invoice.base_invoice_number ? `Part ${invoice.part_sequence} of ${invoice.base_invoice_number}` : `Part ${invoice.part_sequence}`}
                            data-testid={`invoice-part-badge-${invoice.id}`}
                          >
                            Part {invoice.part_sequence}{invoice.base_invoice_number ? ` of ${invoice.base_invoice_number}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 max-w-[120px] truncate">{invoice.customer_name}</td>
                      <td className="px-4 lg:px-6 py-4 hidden lg:table-cell max-w-[180px] truncate" data-testid={`invoice-project-${invoice.id}`}>
                        {invoice.project_reference ? (
                          <div className="text-xs">
                            <div className="font-medium truncate">{invoice.project_reference}</div>
                            {invoice.project_location && <div className="text-muted-foreground truncate">{invoice.project_location}</div>}
                          </div>
                        ) : <span className="text-muted-foreground/60 text-xs">—</span>}
                      </td>
                      <td className="px-4 lg:px-6 py-4 hidden sm:table-cell">{invoice.date}</td>
                      <td className="px-4 lg:px-6 py-4 hidden md:table-cell">{invoice.due_date || '-'}</td>
                      <td className="px-4 lg:px-6 py-4 text-right font-mono" data-testid={`invoice-payable-${invoice.id}`}>
                        {formatCurrency(invoice.final_payable ?? invoice.total)}
                        {invoice.final_payable != null && Math.abs(Number(invoice.final_payable) - Number(invoice.total || 0)) > 0.009 && (
                          <div className="text-[11px] text-muted-foreground" data-testid={`invoice-gross-${invoice.id}`}>
                            of {formatCurrency(invoice.total)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4">
                        <Badge variant="outline" className={statusColors[invoice.status]}>
                          {statusLabels[invoice.status] || invoice.status}
                        </Badge>
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-right">
                        <div className="flex justify-end">
                          <RowActionsMenu
                            testid={`invoice-${invoice.id}`}
                            actions={[
                              { id: 'preview-pdf', label: 'Preview PDF', icon: Eye, onClick: () => handlePreviewPdf(invoice.id, invoice.document_number, 'invoice') },
                              { id: 'download-pdf', label: 'Download PDF', icon: Download, onClick: () => handleDownloadPdf(invoice.id, invoice.document_number) },
                              { id: 'attachments', label: 'Attachments', icon: Paperclip, onClick: () => openAttachmentModal(invoice, 'invoice') },
                              // Unlink Quotation — only shows on invoices actually linked to a quotation.
                              (invoice.quotation_id || invoice.linked_quotation_id) && canEdit && {
                                id: 'unlink-quotation',
                                label: 'Unlink Quotation',
                                icon: XCircle,
                                onClick: () => handleUnlinkQuotation(invoice),
                              },
                              canCreate && { id: 'duplicate', label: 'Duplicate Invoice', icon: CopySimple, onClick: () => handleDuplicateInvoice(invoice) },
                              ((invoice.status === 'draft' || invoice.status === 'rejected') && canEdit) && 'separator',
                              ((invoice.status === 'draft' || invoice.status === 'rejected') && canEdit) && { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => openWorkflowModal('edit', invoice, 'invoice') },
                              (invoice.status === 'draft' && canEdit) && { id: 'submit', label: 'Submit for Approval', icon: PaperPlaneTilt, onClick: () => openWorkflowModal('submit', invoice, 'invoice') },
                              (invoice.status === 'pending_approval' && canEdit) && { id: 'approve', label: 'Approve', icon: CheckCircle, onClick: () => openWorkflowModal('approve', invoice, 'invoice') },
                              (invoice.status === 'pending_approval' && canEdit) && { id: 'reject', label: 'Reject', icon: XCircle, onClick: () => openWorkflowModal('reject', invoice, 'invoice'), destructive: true },
                              (invoice.status === 'approved' && canEdit) && { id: 'mark-paid', label: 'Mark as Paid', icon: CheckCircle, onClick: () => handleMarkPaid(invoice.id) },
                              canEdit && 'separator',
                              canEdit && { id: 'delete', label: ['approved','paid','partial','partially_paid','sent_to_client','client_approved'].includes((invoice.status||'').toLowerCase()) ? 'Delete (DANGER — locked)' : 'Delete', icon: Trash, onClick: () => openWorkflowModal('delete', invoice, 'invoice'), destructive: true },
                            ].filter(Boolean)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invoices.length === 0 && (
              <div className="text-center py-12">
                <Receipt size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No invoices yet</p>
              </div>
            )}
          </motion.div>
        </TabsContent>

        <TabsContent value="quotations" className="mt-4">
          <DocSummary
            kind="quotations"
            label={`Quotations${quotationStatusFilter !== 'all' ? ` — ${quotationStatusFilter.replace(/_/g, ' ')}` : ''}`}
            filters={{
              q: searchTerm,
              // Only pass status when a real filter is chosen. "all" means no
              // status constraint on the summary aggregation and PDF/Excel.
              ...(quotationStatusFilter !== 'all' ? { status: quotationStatusFilter } : {}),
            }}
          />
          {/* Status filter + Show-all-revisions toggle. The status here drives
              BOTH the visible list below AND the DocSummary bar above (KPIs,
              PDF, Excel), so the printed summary matches what the user sees. */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="quotation-filter-bar">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground">Status:</label>
              <select
                value={quotationStatusFilter}
                onChange={(e) => setQuotationStatusFilter(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="quotation-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="sent">Sent</option>
                <option value="sent_to_client">Sent to Client</option>
                <option value="client_approved">Approved by Client</option>
                <option value="agreement_signed">Agreement Signed</option>
                <option value="lpo_received">LPO Received</option>
                <option value="invoiced">Invoiced</option>
                <option value="on_hold">On Hold</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {quotationStatusFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setQuotationStatusFilter('all')}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="quotation-status-filter-clear"
                >
                  Clear
                </button>
              )}
              {/* One-click normaliser for legacy `sent` values. Reads well next
                  to the status dropdown because it's a data-hygiene action on
                  the same dimension. Admin-only; regular users don't see it. */}
              {['admin', 'super_admin'].includes((user?.role || '').toLowerCase()) && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API_URL}/quotations/heal/normalize-legacy-status`);
                      toast.success(res.data.message);
                      fetchData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to normalise');
                    }
                  }}
                  data-testid="quotation-normalize-legacy-btn"
                  title='Renames any old "sent" quotation → "sent_to_client" so filters and reports show them under the canonical status.'
                >
                  Normalise legacy statuses
                </button>
              )}
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAllRevisions}
                onChange={(e) => setShowAllRevisions(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-amber-500"
                data-testid="quotation-show-all-revisions-checkbox"
              />
              Show all revisions
              <span className="text-[10px] text-muted-foreground/70">(originals + R1, R2… — every version editable)</span>
            </label>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Quotation #</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Customer</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden lg:table-cell">Project</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden sm:table-cell">Date</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground hidden md:table-cell">Valid Until</th>
                    <th className="text-right px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Total</th>
                    <th className="text-left px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Status</th>
                    <th className="text-right px-4 lg:px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {quotations.filter(quo => {
                    // Status filter — must match the DocSummary bar above so
                    // what you see is what you print.
                    if (quotationStatusFilter !== 'all') {
                      if ((quo.status || 'draft').toLowerCase() !== quotationStatusFilter) return false;
                    }
                    if (!searchTerm) return true;
                    // Multi-token AND search (same rule as Invoices tab).
                    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
                    const haystack = [
                      quo.document_number, quo.customer_name,
                      quo.project_reference, quo.project_location,
                      quo.reference, quo.salutation, quo.created_by_name,
                    ].filter(Boolean).join(' ').toLowerCase();
                    return tokens.every(t => haystack.includes(t));
                  }).map((quotation) => {
                    const rev = quotation.revision || 'R0';
                    const isLatest = quotation.is_latest_revision !== false;
                    const displayNumber = rev && rev !== 'R0'
                      ? `${quotation.document_number} (${rev})`
                      : `${quotation.document_number}${rev === 'R0' ? ' (Original)' : ''}`;
                    return (
                    <tr
                      key={quotation.id}
                      className={`hover:bg-muted/30 transition-colors ${!isLatest ? 'bg-amber-50/30 dark:bg-amber-900/5' : ''}`}
                      data-testid={`quotation-row-${quotation.id}`}
                    >
                      <td className="px-4 lg:px-6 py-4 font-mono text-sm">
                        <span data-testid={`quotation-number-${quotation.id}`}>{displayNumber}</span>
                        {!isLatest && (
                          <span
                            className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                            title="Older revision — still editable; further edits will create the next revision once you switch to a locked status"
                          >
                            Older rev
                          </span>
                        )}
                        {isLatest && rev !== 'R0' && (
                          <span
                            className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                            title="Latest revision"
                          >
                            Latest
                          </span>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 max-w-[120px] truncate">{quotation.customer_name}</td>
                      <td className="px-4 lg:px-6 py-4 hidden lg:table-cell max-w-[180px] truncate" data-testid={`quotation-project-${quotation.id}`}>
                        {quotation.project_reference ? (
                          <div className="text-xs">
                            <div className="font-medium truncate">{quotation.project_reference}</div>
                            {quotation.project_location && <div className="text-muted-foreground truncate">{quotation.project_location}</div>}
                          </div>
                        ) : <span className="text-muted-foreground/60 text-xs">—</span>}
                      </td>
                      <td className="px-4 lg:px-6 py-4 hidden sm:table-cell">{quotation.date}</td>
                      <td className="px-4 lg:px-6 py-4 hidden md:table-cell">{quotation.valid_until || '-'}</td>
                      <td className="px-4 lg:px-6 py-4 text-right font-mono">{formatCurrency(quotation.total)}</td>
                      <td className="px-4 lg:px-6 py-4">
                        <Badge variant="outline" className={statusColors[quotation.status]}>
                          {statusLabels[quotation.status] || quotation.status}
                        </Badge>
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-right">
                        <div className="flex justify-end items-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Actions" data-testid={`quo-actions-${quotation.id}`}>
                                <DotsThreeVertical size={18} weight="bold" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel>{quotation.document_number}</DropdownMenuLabel>
                              <DropdownMenuSeparator />

                              {/* View / PDF actions — always available */}
                              <DropdownMenuItem onClick={() => handlePreviewPdf(quotation.id, quotation.document_number, 'quotation')} data-testid={`preview-quo-pdf-${quotation.id}`}>
                                <Eye size={14} className="mr-2" /> Preview PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadQuotationPdf(quotation.id, quotation.document_number)} data-testid={`download-quo-pdf-${quotation.id}`}>
                                <Download size={14} className="mr-2" /> Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openAttachmentModal(quotation, 'quotation')} data-testid={`attachments-quo-${quotation.id}`}>
                                <Paperclip size={14} className="mr-2" /> Attachments
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              {/* Edit, Costing, Duplicate */}
                              {isQuoteEditable(quotation.status) && canEdit && (
                                <DropdownMenuItem onClick={() => openWorkflowModal('edit', quotation, 'quotation')} data-testid={`edit-quo-${quotation.id}`}>
                                  <PencilSimple size={14} className="mr-2" /> Edit Quotation
                                </DropdownMenuItem>
                              )}
                              {!isQuoteEditable(quotation.status) && canEdit && (
                                <DropdownMenuItem onClick={() => openWorkflowModal('edit', quotation, 'quotation')} data-testid={`edit-quo-${quotation.id}`}>
                                  <PencilSimple size={14} className="mr-2" /> Edit (creates revision)
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => navigate(`/dashboard/sales/quotations/${quotation.id}/costing`)} data-testid={`costing-quo-${quotation.id}`}>
                                <Receipt size={14} className="mr-2" /> Costing Sheet
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDiffViewer({ open: true, quotationId: quotation.id, documentNumber: quotation.document_number })}
                                data-testid={`compare-revisions-quo-${quotation.id}`}
                              >
                                <ClockClockwise size={14} className="mr-2" /> Compare Revisions
                              </DropdownMenuItem>
                              {canCreate && (
                                <DropdownMenuItem onClick={() => handleDuplicateQuotation(quotation)} data-testid={`duplicate-quo-${quotation.id}`}>
                                  <CopySimple size={14} className="mr-2" /> Duplicate Quotation
                                </DropdownMenuItem>
                              )}

                              {/* Status transitions — only the ones valid for current status */}
                              {(quotation.status === 'draft' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => openWorkflowModal('submit', quotation, 'quotation')} data-testid={`submit-quo-${quotation.id}`}>
                                    <PaperPlaneTilt size={14} className="mr-2" /> Submit for Approval
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'pending_approval' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => openWorkflowModal('approve', quotation, 'quotation')} className="text-green-600 focus:text-green-700">
                                    <CheckCircle size={14} className="mr-2" /> Approve (Internal)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openWorkflowModal('reject', quotation, 'quotation')} className="text-red-600 focus:text-red-700">
                                    <XCircle size={14} className="mr-2" /> Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'approved' && canCreate) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'sent_to_client')} className="text-purple-600 focus:text-purple-700" data-testid={`send-quo-${quotation.id}`}>
                                    <PaperPlaneTilt size={14} className="mr-2" /> Send to Client
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'sent_to_client' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'client_approved')} className="text-emerald-600 focus:text-emerald-700">
                                    <CheckCircle size={14} className="mr-2" /> Client Approved
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'agreement_signed')} className="text-green-600 focus:text-green-700">
                                    <CheckCircle size={14} className="mr-2" /> Agreement Signed
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'lpo_received')} className="text-cyan-600 focus:text-cyan-700">
                                    <CheckCircle size={14} className="mr-2" /> LPO Received
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'on_hold')} className="text-orange-600 focus:text-orange-700">
                                    <Clock size={14} className="mr-2" /> On Hold
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'closed')} className="text-zinc-600 focus:text-zinc-700">
                                    <XCircle size={14} className="mr-2" /> Close
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'client_approved' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'agreement_signed')} className="text-green-600 focus:text-green-700">
                                    <CheckCircle size={14} className="mr-2" /> Agreement Signed
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'lpo_received')} className="text-cyan-600 focus:text-cyan-700">
                                    <CheckCircle size={14} className="mr-2" /> LPO Received
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'agreement_signed' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'lpo_received')} className="text-cyan-600 focus:text-cyan-700">
                                    <CheckCircle size={14} className="mr-2" /> LPO Received
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(quotation.status === 'on_hold' && canEdit) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuLabel className="text-xs text-muted-foreground">Update Status</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'sent_to_client')} className="text-purple-600 focus:text-purple-700">
                                    <PaperPlaneTilt size={14} className="mr-2" /> Re-send to Client
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleTransition(quotation, 'quotation', 'closed')} className="text-zinc-600 focus:text-zinc-700">
                                    <XCircle size={14} className="mr-2" /> Close
                                  </DropdownMenuItem>
                                </>
                              )}
                              {/* Convert to Invoice or Link an existing one.
                                  Shown on any status where a sale is confirmed
                                  (sent → client_approved → lpo_received etc.)
                                  so old quotations stuck on legacy statuses
                                  can still be converted or retro-linked
                                  without hitting the DB directly. */}
                              {/* Payment Ledger — always available, shows
                                  consolidated invoiced / received / balance
                                  across every invoice tied to this quote. */}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openLedger(quotation.id)} className="text-emerald-700 focus:text-emerald-800 font-medium" data-testid={`ledger-quo-${quotation.id}`}>
                                <ChartBar size={14} className="mr-2" /> Payment Ledger
                              </DropdownMenuItem>

                              {['approved', 'sent', 'sent_to_client', 'client_approved',
                                'agreement_signed', 'lpo_received',
                                // Feb-2026: once the first part invoice is
                                // raised, backend flips quotation.status to
                                // ``part_invoiced``. Without this in the
                                // whitelist the kebab hid the "Part Invoice"
                                // option after INV-XXXX-1, blocking every
                                // subsequent round on the same quote
                                // (QT-47 HUDA regression).
                                'part_invoiced', 'partial_invoiced',
                                'partially_invoiced',
                                // Also allow when the quote is flagged
                                // ``invoiced`` — either a full invoice was
                                // raised OR earlier part-invoices hit
                                // cumulative 100 %. Backend enforces
                                // cumulative-% ≤ 100 per line, so opening
                                // the dialog is safe: if there's genuinely
                                // no billable balance the user will see 0 %
                                // available and can void the existing
                                // invoice(s) first if they need to re-bill.
                                'invoiced',
                              ].includes(quotation.status) && canCreate && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleConvertToInvoice(quotation.id)} className="text-indigo-600 focus:text-indigo-700 font-medium" data-testid={`convert-quo-${quotation.id}`}>
                                    <ArrowRight size={14} className="mr-2" /> Create Invoice
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openPartInvoiceDialog(quotation.id)} className="text-indigo-600 focus:text-indigo-700 font-medium" data-testid={`part-invoice-quo-${quotation.id}`}>
                                    <FileText size={14} className="mr-2" /> Create Part Invoice…
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openLinkInvoiceDialog(quotation)} className="text-indigo-600 focus:text-indigo-700" data-testid={`link-quo-${quotation.id}`}>
                                    <Receipt size={14} className="mr-2" /> Link Existing Invoice…
                                  </DropdownMenuItem>
                                </>
                              )}

                              {/* Delete — draft/rejected for everyone; any other status (including
                                  unwanted R1/R2 revisions) is admin-only and force-deleted via the
                                  same workflow modal. Latest revision logic on the backend
                                  auto-promotes the previous sibling so the tree stays navigable. */}
                              {(() => {
                                const isDraftOrRejected = quotation.status === 'draft' || quotation.status === 'rejected';
                                const isRevision = (quotation.revision && quotation.revision !== 'R0') || quotation.parent_document_id;
                                const isAdmin = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
                                if (!canEdit) return null;
                                if (!isDraftOrRejected && !isRevision) return null;
                                if (!isDraftOrRejected && isRevision && !isAdmin) return null;
                                return (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => openWorkflowModal('delete', quotation, 'quotation')}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`delete-quo-${quotation.id}`}
                                    >
                                      <Trash size={14} className="mr-2" />
                                      {isRevision && !isDraftOrRejected
                                        ? `Delete revision ${quotation.revision || 'R?'}`
                                        : 'Delete'}
                                    </DropdownMenuItem>
                                  </>
                                );
                              })()}

                              {/* Rejected note */}
                              {quotation.status === 'rejected' && quotation.rejection_note && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 py-2 text-xs text-red-600 dark:text-red-400">
                                    <div className="font-medium mb-0.5 flex items-center gap-1"><Warning size={12} /> Rejection note</div>
                                    <div className="text-muted-foreground">{quotation.rejection_note}</div>
                                  </div>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {quotations.length === 0 && (
              <div className="text-center py-12">
                <FileText size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No quotations yet</p>
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* PDF Preview Modal */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(open) => {
        setPdfPreviewOpen(open);
        if (!open) {
          // Clean up PDF URL when closing
          if (pdfPreviewUrl) {
            window.URL.revokeObjectURL(pdfPreviewUrl);
          }
          setPdfPreviewUrl('');
          setPdfPreviewTitle('');
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FilePdf size={24} className="text-red-500" />
                {pdfPreviewTitle}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open(pdfPreviewUrl, '_blank')}>
                  <Eye size={16} className="mr-1" /> Open in New Tab
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfPreviewUrl;
                  link.download = `${pdfPreviewTitle}.pdf`;
                  link.click();
                }}>
                  <Download size={16} className="mr-1" /> Download
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 h-full">
            <iframe
              src={pdfPreviewUrl}
              className="w-full h-[calc(90vh-80px)] border-0"
              title="PDF Preview"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachment Manager - Enhanced with multi-upload, preview, download */}
      <AttachmentManager
        open={attachmentModalOpen}
        onOpenChange={setAttachmentModalOpen}
        documentId={selectedDocForAttachment?.id}
        documentNumber={selectedDocForAttachment?.document_number}
        documentType={attachmentDocType}
      />

      {/* Mark-as-Paid Dialog — collects payment mode + bank/cash account */}
      <Dialog open={markPaidOpen} onOpenChange={(open) => {
        setMarkPaidOpen(open);
        if (!open) { setMarkPaidInvoice(null); }
      }}>
        <DialogContent className="max-w-lg" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" /> Record Payment
            </DialogTitle>
            <DialogDescription>
              Mark <span className="font-semibold">{markPaidInvoice?.document_number}</span> as paid and post the receipt to the chosen ledger account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={markPaidForm.payment_date}
                  onChange={(e) => setMarkPaidForm({ ...markPaidForm, payment_date: e.target.value })}
                  data-testid="mark-paid-date"
                />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select
                  value={markPaidForm.payment_method}
                  onValueChange={(v) => setMarkPaidForm({ ...markPaidForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="mark-paid-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="online">Online / UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Deposit to Account *</Label>
              <Select
                value={markPaidForm.account_id}
                onValueChange={(v) => setMarkPaidForm({ ...markPaidForm, account_id: v })}
              >
                <SelectTrigger data-testid="mark-paid-account">
                  <SelectValue placeholder="Select cash or bank account" />
                </SelectTrigger>
                <SelectContent>
                  {cashBankAccounts.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No cash/bank accounts found — will fall back to default Cash (1000).</div>
                  )}
                  {cashBankAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.code} · {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">The receipt voucher will post the payment to this account and the balance will reflect in the Bank/Cash statement and dashboard immediately.</p>
            </div>

            {markPaidForm.payment_method === 'cheque' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cheque Number</Label>
                  <Input
                    value={markPaidForm.cheque_number}
                    onChange={(e) => setMarkPaidForm({ ...markPaidForm, cheque_number: e.target.value })}
                    placeholder="e.g. 123456"
                    data-testid="mark-paid-cheque-no"
                  />
                </div>
                <div>
                  <Label>Cheque Date</Label>
                  <Input
                    type="date"
                    value={markPaidForm.cheque_date}
                    onChange={(e) => setMarkPaidForm({ ...markPaidForm, cheque_date: e.target.value })}
                    data-testid="mark-paid-cheque-date"
                  />
                </div>
              </div>
            )}

            {markPaidInvoice && (() => {
              const total = invoiceBilled(markPaidInvoice);
              const prior = Number(markPaidInvoice.amount_paid || 0);
              const balance = Math.max(0, invoiceBilled(markPaidInvoice) - prior);
              const isPartial = markPaidForm.payment_type === 'partial';
              const enteredAmt = parseFloat(markPaidForm.settlement_amount || '0') || 0;
              const settling = isPartial ? Math.min(enteredAmt, balance) : balance;
              const afterBalance = Math.max(0, total - prior - settling);
              return (
                <>
                  <div className="space-y-2">
                    <Label>Payment Type *</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setMarkPaidForm({ ...markPaidForm, payment_type: 'final', settlement_amount: balance.toFixed(2) })}
                        className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                          markPaidForm.payment_type !== 'partial'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-input bg-background hover:bg-accent/30'
                        }`}
                        data-testid="mark-paid-type-final"
                      >
                        Final Payment
                        <div className="text-xs font-normal text-muted-foreground">Settle the full balance</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMarkPaidForm({ ...markPaidForm, payment_type: 'partial' })}
                        className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                          markPaidForm.payment_type === 'partial'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-input bg-background hover:bg-accent/30'
                        }`}
                        data-testid="mark-paid-type-partial"
                      >
                        Part Payment
                        <div className="text-xs font-normal text-muted-foreground">Pay a portion now</div>
                      </button>
                    </div>
                  </div>

                  {isPartial && (
                    <div className="space-y-2">
                      <Label htmlFor="mark-paid-amount">Amount to Settle Now *</Label>
                      <Input
                        id="mark-paid-amount"
                        type="number"
                        min="0.01"
                        max={balance}
                        step="0.01"
                        value={markPaidForm.settlement_amount}
                        onChange={(e) => setMarkPaidForm({ ...markPaidForm, settlement_amount: e.target.value })}
                        placeholder="e.g. 1000.00"
                        data-testid="mark-paid-amount"
                      />
                      <p className="text-xs text-muted-foreground">
                        Outstanding balance: <span className="font-mono">{formatCurrency(balance)}</span> — invoice will remain in <strong>Partial</strong> status until fully paid.
                      </p>
                    </div>
                  )}

                  <div className="rounded-md bg-muted/40 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span>Amount Payable (this invoice):</span><span className="font-semibold">{formatCurrency(total)}</span></div>
                    <div className="flex justify-between"><span>Amount Paid (prior):</span><span>{formatCurrency(prior)}</span></div>
                    <div className="flex justify-between text-green-700 font-semibold mt-1"><span>Settling now:</span><span>{formatCurrency(settling)}</span></div>
                    <div className="flex justify-between text-amber-700 font-semibold border-t pt-1 mt-1">
                      <span>{afterBalance > 0.009 ? 'Balance after:' : 'Status after:'}</span>
                      <span>{afterBalance > 0.009 ? formatCurrency(afterBalance) : 'PAID IN FULL'}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)} disabled={markPaidLoading}>Cancel</Button>
            <Button
              onClick={confirmMarkPaid}
              disabled={markPaidLoading || !markPaidForm.account_id && cashBankAccounts.length > 0}
              data-testid="mark-paid-confirm"
            >
              {markPaidLoading ? 'Posting…' : 'Confirm & Post Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Modal - Submit, Approve, Reject, Edit, Delete */}
      <Dialog open={workflowModalOpen} onOpenChange={(open) => {
        setWorkflowModalOpen(open);
        if (!open) {
          // Clean up state when closing
          setWorkflowAction(null);
          setWorkflowDoc(null);
          setWorkflowNote('');
          setEditFormData(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {workflowAction === 'submit' && <><PaperPlaneTilt size={20} className="text-blue-600" /> Submit for Approval</>}
              {workflowAction === 'approve' && <><CheckCircle size={20} className="text-green-600" /> Approve Document</>}
              {workflowAction === 'reject' && <><XCircle size={20} className="text-red-600" /> Reject Document</>}
              {workflowAction === 'edit' && <><PencilSimple size={20} className="text-amber-600" /> Edit Document</>}
              {workflowAction === 'delete' && <><Trash size={20} className="text-red-600" /> Delete Document</>}
            </DialogTitle>
            <DialogDescription>
              {workflowDoc?.document_number || workflowDoc?.quotation_number} - {workflowDoc?.customer_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Document Info */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Total Amount:</span>
                <span className="font-mono font-bold">{formatCurrency(workflowDoc?.total || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span>Current Status:</span>
                <Badge variant="outline" className={statusColors[workflowDoc?.status]}>
                  {workflowDoc?.status?.replace('_', ' ')}
                </Badge>
              </div>
            </div>

            {/* Rejection Note for Rejected Documents */}
            {workflowDoc?.status === 'rejected' && workflowDoc?.rejection_note && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">Previous Rejection Reason:</p>
                <p className="text-sm text-red-600 dark:text-red-300 mt-1">{workflowDoc.rejection_note}</p>
              </div>
            )}

            {/* Note Input - for submit, approve, reject, delete */}
            {['submit', 'approve', 'reject', 'delete'].includes(workflowAction) && (
              <div>
                <Label>
                  {workflowAction === 'submit' && 'Note for Approver (optional)'}
                  {workflowAction === 'approve' && 'Approval Note (optional)'}
                  {workflowAction === 'reject' && 'Rejection Reason (required)'}
                  {workflowAction === 'delete' && 'Deletion Reason (optional)'}
                </Label>
                <Textarea
                  value={workflowNote}
                  onChange={(e) => setWorkflowNote(e.target.value)}
                  placeholder={
                    workflowAction === 'reject' 
                      ? 'Please provide a reason for rejection...' 
                      : 'Add a note...'
                  }
                  className="mt-1"
                  rows={3}
                  required={workflowAction === 'reject'}
                  data-testid="workflow-note-input"
                />
              </div>
            )}

            {/* Edit Form - simplified */}
            {workflowAction === 'edit' && editFormData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={editFormData.date || ''}
                      onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{workflowDoc?.docType === 'invoice' ? 'Due Date' : 'Valid Until'}</Label>
                    <Input
                      type="date"
                      value={editFormData.due_date || editFormData.valid_until || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        [workflowDoc?.docType === 'invoice' ? 'due_date' : 'valid_until']: e.target.value
                      })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input
                    value={editFormData.reference || ''}
                    onChange={(e) => setEditFormData({...editFormData, reference: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    rows={2}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: To edit line items, please create a new document.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWorkflowModalOpen(false)} disabled={workflowLoading}>
              Cancel
            </Button>
            
            {workflowAction === 'submit' && (
              <Button onClick={handleSubmitForApproval} disabled={workflowLoading} data-testid="confirm-submit-btn">
                {workflowLoading ? <CircleNotch size={16} className="animate-spin mr-1" /> : <PaperPlaneTilt size={16} className="mr-1" />}
                Submit for Approval
              </Button>
            )}
            
            {workflowAction === 'approve' && (
              <Button onClick={() => handleApprovalAction('approve')} disabled={workflowLoading} className="bg-green-600 hover:bg-green-700" data-testid="confirm-approve-btn">
                {workflowLoading ? <CircleNotch size={16} className="animate-spin mr-1" /> : <CheckCircle size={16} className="mr-1" />}
                Approve
              </Button>
            )}
            
            {workflowAction === 'reject' && (
              <Button onClick={() => handleApprovalAction('reject')} disabled={workflowLoading || !workflowNote} variant="destructive" data-testid="confirm-reject-btn">
                {workflowLoading ? <CircleNotch size={16} className="animate-spin mr-1" /> : <XCircle size={16} className="mr-1" />}
                Reject
              </Button>
            )}
            
            {workflowAction === 'edit' && (
              <Button onClick={handleEditDocument} disabled={workflowLoading} data-testid="confirm-edit-btn">
                {workflowLoading ? <CircleNotch size={16} className="animate-spin mr-1" /> : <PencilSimple size={16} className="mr-1" />}
                Save Changes
              </Button>
            )}
            
            {workflowAction === 'delete' && (
              <Button onClick={handleDeleteDocument} disabled={workflowLoading} variant="destructive" data-testid="confirm-delete-btn">
                {workflowLoading ? <CircleNotch size={16} className="animate-spin mr-1" /> : <Trash size={16} className="mr-1" />}
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Approvals Badge in Header */}
      {pendingApprovals.length > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <Button
            onClick={() => setShowApprovalsQueue(!showApprovalsQueue)}
            className="rounded-full shadow-lg bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="pending-approvals-fab"
          >
            <Clock size={20} className="mr-2" />
            {pendingApprovals.length} Pending Approval{pendingApprovals.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}
    </div>
  );
}
